/**
 * BASE DE DONNÉES COMPLÈTE DES SCORES MÉDICAUX
 * 60+ scores cliniques répartis en 17 catégories
 * Avec cotations CCAM/NGAP françaises
 */

export const SCORES_DATABASE = {
  // ============================================
  // 1. CARDIOLOGIE / MÉDECINE VASCULAIRE
  // ============================================
  cardiologie: {
    name: "Cardiologie / Médecine Vasculaire",
    icon: "Heart",
    color: "from-red-600 to-rose-600",
    scores: [
      {
        id: "chadsvasc",
        name: "CHA₂DS₂-VASc",
        fullName: "Score de risque thromboembolique (FA)",
        description: "Indication anticoagulation dans la fibrillation atriale",
        cotation: { code: "DEQP003", libelle: "ECG + consultation", tarif: 37.92 },
        fields: [
          { id: "ic", label: "Insuffisance cardiaque / FEVG ≤40%", type: "boolean", points: 1 },
          { id: "hta", label: "Hypertension artérielle", type: "boolean", points: 1 },
          { id: "age75", label: "Âge ≥ 75 ans", type: "boolean", points: 2 },
          { id: "diabete", label: "Diabète", type: "boolean", points: 1 },
          { id: "avc", label: "AVC / AIT / Thromboembolie", type: "boolean", points: 2 },
          { id: "vasculaire", label: "Maladie vasculaire (IDM, AOMI, plaque aortique)", type: "boolean", points: 1 },
          { id: "age65", label: "Âge 65-74 ans", type: "boolean", points: 1 },
          { id: "femme", label: "Sexe féminin", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score === 0) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "Pas d'anticoagulation recommandée" };
          if (score === 1) return { level: "modere", text: `Risque modéré (${score})`, color: "text-yellow-400", action: "Anticoagulation à considérer (AOD préféré)" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Anticoagulation recommandée (AOD ou AVK, INR 2-3)" };
        }
      },
      {
        id: "hasbled",
        name: "HAS-BLED",
        fullName: "Score de risque hémorragique",
        description: "Risque de saignement sous anticoagulant",
        cotation: null,
        fields: [
          { id: "hta", label: "HTA non contrôlée (PAS >160 mmHg)", type: "boolean", points: 1 },
          { id: "renal", label: "Insuffisance rénale (dialyse, greffe, créat >200)", type: "boolean", points: 1 },
          { id: "hepatique", label: "Insuffisance hépatique (cirrhose, bilirubine 2xN)", type: "boolean", points: 1 },
          { id: "avc", label: "Antécédent d'AVC", type: "boolean", points: 1 },
          { id: "saignement", label: "Antécédent/prédisposition au saignement", type: "boolean", points: 1 },
          { id: "inr", label: "INR instables (<60% dans la cible)", type: "boolean", points: 1 },
          { id: "age65", label: "Âge > 65 ans", type: "boolean", points: 1 },
          { id: "medicaments", label: "Médicaments (AINS, antiagrégants)", type: "boolean", points: 1 },
          { id: "alcool", label: "Alcool (≥8 verres/semaine)", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score <= 2) return { level: "faible", text: `Risque hémorragique acceptable (${score})`, color: "text-green-400", action: "Anticoagulation possible, surveillance standard" };
          return { level: "eleve", text: `Risque hémorragique élevé (${score})`, color: "text-red-400", action: "Prudence! Corriger facteurs modifiables, surveillance rapprochée, préférer AOD" };
        }
      },
      {
        id: "score2",
        name: "SCORE2 / SCORE2-OP",
        fullName: "Risque cardiovasculaire à 10 ans (ESC 2021)",
        description: "Prévention cardiovasculaire primaire",
        cotation: { code: "C + DEQP003", libelle: "Consultation + ECG", tarif: 48.50 },
        fields: [
          { id: "age", label: "Âge (ans)", type: "number", min: 40, max: 89 },
          { id: "sexe", label: "Sexe", type: "select", options: [{ value: "homme", label: "Homme" }, { value: "femme", label: "Femme" }] },
          { id: "tabac", label: "Tabagisme actif", type: "boolean" },
          { id: "pas", label: "Pression artérielle systolique (mmHg)", type: "number", min: 100, max: 200 },
          { id: "cholesterol", label: "Cholestérol non-HDL (g/L)", type: "number", min: 0.8, max: 4, step: 0.1 }
        ],
        calculate: (values) => {
          let baseRisk = values.sexe === "homme" ? 4 : 2;
          baseRisk += (values.age - 40) * 0.25;
          if (values.tabac) baseRisk *= 1.8;
          baseRisk += (values.pas - 120) * 0.04;
          baseRisk += (values.cholesterol - 1.5) * 1.5;
          return Math.min(Math.max(Math.round(baseRisk * 10) / 10, 1), 30);
        },
        interpretation: (score, values) => {
          const age = values?.age || 50;
          let thresholds;
          if (age < 50) thresholds = { low: 2.5, high: 7.5 };
          else if (age < 70) thresholds = { low: 5, high: 10 };
          else thresholds = { low: 7.5, high: 15 };
          
          if (score < thresholds.low) return { level: "faible", text: `Risque faible (${score}%)`, color: "text-green-400", action: "Conseils hygiéno-diététiques" };
          if (score < thresholds.high) return { level: "modere", text: `Risque modéré (${score}%)`, color: "text-yellow-400", action: "Modification mode de vie, envisager statine si échec" };
          return { level: "eleve", text: `Risque élevé (${score}%)`, color: "text-red-400", action: "Statine recommandée, objectif LDL selon risque" };
        }
      },
      {
        id: "heart",
        name: "HEART Score",
        fullName: "History, ECG, Age, Risk factors, Troponin",
        description: "Stratification des douleurs thoraciques aux urgences",
        cotation: { code: "DEQP003 + Bio", libelle: "ECG + Troponine", tarif: 30.00 },
        fields: [
          { id: "histoire", label: "Histoire clinique", type: "select", options: [
            { value: 0, label: "Peu suspecte" }, { value: 1, label: "Modérément suspecte" }, { value: 2, label: "Très suspecte" }
          ]},
          { id: "ecg", label: "ECG", type: "select", options: [
            { value: 0, label: "Normal" }, { value: 1, label: "Anomalies non spécifiques" }, { value: 2, label: "Déviation ST significative" }
          ]},
          { id: "age", label: "Âge", type: "select", options: [
            { value: 0, label: "< 45 ans" }, { value: 1, label: "45-64 ans" }, { value: 2, label: "≥ 65 ans" }
          ]},
          { id: "fdr", label: "Facteurs de risque", type: "select", options: [
            { value: 0, label: "Aucun" }, { value: 1, label: "1-2 FDR" }, { value: 2, label: "≥3 FDR ou ATCD coronarien" }
          ]},
          { id: "troponine", label: "Troponine", type: "select", options: [
            { value: 0, label: "Normale" }, { value: 1, label: "1-3x normale" }, { value: 2, label: "> 3x normale" }
          ]}
        ],
        interpretation: (score) => {
          if (score <= 3) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "Sortie possible après 2ème troponine négative" };
          if (score <= 6) return { level: "modere", text: `Risque intermédiaire (${score})`, color: "text-yellow-400", action: "Hospitalisation, bilan complémentaire (coroscanner ou coronarographie)" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "USIC, coronarographie précoce" };
        }
      },
      {
        id: "wells_tvp",
        name: "Wells TVP",
        fullName: "Score de Wells - Thrombose Veineuse Profonde",
        description: "Probabilité clinique de TVP",
        cotation: { code: "EJQM001", libelle: "Écho-Doppler veineux MI", tarif: 51.80 },
        fields: [
          { id: "cancer", label: "Cancer actif (traitement < 6 mois ou palliatif)", type: "boolean", points: 1 },
          { id: "paralysie", label: "Paralysie, parésie ou immobilisation plâtrée MI", type: "boolean", points: 1 },
          { id: "alitement", label: "Alitement > 3 jours ou chirurgie < 12 semaines", type: "boolean", points: 1 },
          { id: "douleur", label: "Douleur/sensibilité le long du trajet veineux", type: "boolean", points: 1 },
          { id: "oedeme", label: "Œdème de tout le membre inférieur", type: "boolean", points: 1 },
          { id: "mollet", label: "Mollet > 3 cm par rapport au côté sain", type: "boolean", points: 1 },
          { id: "godet", label: "Œdème prenant le godet", type: "boolean", points: 1 },
          { id: "colaterales", label: "Veines collatérales superficielles", type: "boolean", points: 1 },
          { id: "atcd_tvp", label: "Antécédent documenté de TVP", type: "boolean", points: 1 },
          { id: "diagnostic_alternatif", label: "Diagnostic alternatif au moins aussi probable", type: "boolean", points: -2 }
        ],
        interpretation: (score) => {
          if (score <= 0) return { level: "faible", text: `Probabilité faible (${score})`, color: "text-green-400", action: "D-Dimères. Si négatifs: TVP exclue" };
          if (score <= 2) return { level: "modere", text: `Probabilité intermédiaire (${score})`, color: "text-yellow-400", action: "D-Dimères. Si positifs: écho-Doppler" };
          return { level: "eleve", text: `Probabilité forte (${score})`, color: "text-red-400", action: "Écho-Doppler d'emblée (ou traitement d'attente)" };
        }
      },
      {
        id: "wells_ep",
        name: "Wells EP",
        fullName: "Score de Wells - Embolie Pulmonaire",
        description: "Probabilité clinique d'embolie pulmonaire",
        cotation: { code: "ECQH010", libelle: "Angioscanner thoracique", tarif: 25.27 },
        fields: [
          { id: "atcd_tvp_ep", label: "Antécédent de TVP ou EP", type: "boolean", points: 1.5 },
          { id: "fc", label: "Fréquence cardiaque > 100/min", type: "boolean", points: 1.5 },
          { id: "chirurgie", label: "Chirurgie ou immobilisation < 4 semaines", type: "boolean", points: 1.5 },
          { id: "hemoptysie", label: "Hémoptysie", type: "boolean", points: 1 },
          { id: "cancer", label: "Cancer actif", type: "boolean", points: 1 },
          { id: "signes_tvp", label: "Signes cliniques de TVP", type: "boolean", points: 3 },
          { id: "diagnostic_ep", label: "Diagnostic alternatif moins probable que l'EP", type: "boolean", points: 3 }
        ],
        interpretation: (score) => {
          if (score <= 4) return { level: "faible", text: `EP peu probable (${score})`, color: "text-green-400", action: "D-Dimères. Si négatifs: EP exclue" };
          return { level: "eleve", text: `EP probable (${score})`, color: "text-red-400", action: "Angioscanner d'emblée (ou HBPM puis scanner)" };
        }
      },
      {
        id: "geneve",
        name: "Genève révisé",
        fullName: "Score de Genève révisé simplifié",
        description: "Alternative au Wells pour l'embolie pulmonaire",
        cotation: { code: "ECQH010", libelle: "Angioscanner thoracique", tarif: 25.27 },
        fields: [
          { id: "age65", label: "Âge > 65 ans", type: "boolean", points: 1 },
          { id: "atcd", label: "Antécédent de TVP ou EP", type: "boolean", points: 1 },
          { id: "chirurgie", label: "Chirurgie ou fracture < 1 mois", type: "boolean", points: 1 },
          { id: "cancer", label: "Cancer actif", type: "boolean", points: 1 },
          { id: "douleur_mi", label: "Douleur unilatérale d'un MI", type: "boolean", points: 1 },
          { id: "hemoptysie", label: "Hémoptysie", type: "boolean", points: 1 },
          { id: "fc75", label: "FC 75-94/min", type: "boolean", points: 1 },
          { id: "fc95", label: "FC ≥ 95/min", type: "boolean", points: 2 },
          { id: "oedeme", label: "Œdème et douleur à la palpation MI", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score <= 2) return { level: "faible", text: `Probabilité faible (${score})`, color: "text-green-400", action: "D-Dimères. Si négatifs: EP exclue" };
          if (score <= 4) return { level: "modere", text: `Probabilité intermédiaire (${score})`, color: "text-yellow-400", action: "D-Dimères. Si positifs: angioscanner" };
          return { level: "eleve", text: `Probabilité forte (${score})`, color: "text-red-400", action: "Angioscanner d'emblée" };
        }
      },
      {
        id: "spesi",
        name: "sPESI",
        fullName: "Simplified Pulmonary Embolism Severity Index",
        description: "Gravité de l'embolie pulmonaire confirmée",
        cotation: null,
        fields: [
          { id: "age80", label: "Âge > 80 ans", type: "boolean", points: 1 },
          { id: "cancer", label: "Cancer", type: "boolean", points: 1 },
          { id: "ic_bpco", label: "Insuffisance cardiaque ou BPCO", type: "boolean", points: 1 },
          { id: "fc110", label: "FC ≥ 110/min", type: "boolean", points: 1 },
          { id: "pas", label: "PAS < 100 mmHg", type: "boolean", points: 1 },
          { id: "spo2", label: "SpO2 < 90%", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score === 0) return { level: "faible", text: "Risque faible (0)", color: "text-green-400", action: "Traitement ambulatoire possible si conditions réunies" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Hospitalisation, surveillance, envisager thrombolyse si instabilité" };
        }
      },
      {
        id: "timi_nstemi",
        name: "TIMI (NSTEMI)",
        fullName: "Thrombolysis In Myocardial Infarction",
        description: "Risque à 14 jours dans le SCA non ST+",
        cotation: { code: "YYYY186", libelle: "Coronarographie", tarif: 255.85 },
        fields: [
          { id: "age65", label: "Âge ≥ 65 ans", type: "boolean", points: 1 },
          { id: "fdr3", label: "≥ 3 facteurs de risque CV", type: "boolean", points: 1 },
          { id: "coronaropathie", label: "Coronaropathie connue (sténose ≥ 50%)", type: "boolean", points: 1 },
          { id: "aspirine", label: "Prise d'aspirine dans les 7 derniers jours", type: "boolean", points: 1 },
          { id: "angor", label: "≥ 2 épisodes angineux dans les 24h", type: "boolean", points: 1 },
          { id: "st", label: "Modification du ST ≥ 0.5mm", type: "boolean", points: 1 },
          { id: "troponine", label: "Élévation des biomarqueurs cardiaques", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score <= 2) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "Mortalité <5%. Stratégie invasive non urgente" };
          if (score <= 4) return { level: "modere", text: `Risque intermédiaire (${score})`, color: "text-yellow-400", action: "Mortalité 5-10%. Coronarographie < 72h" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Mortalité >10%. Coronarographie < 24h" };
        }
      },
      {
        id: "grace",
        name: "GRACE",
        fullName: "Global Registry of Acute Coronary Events",
        description: "Pronostic du syndrome coronarien aigu",
        cotation: null,
        fields: [
          { id: "age", label: "Âge (ans)", type: "number", min: 18, max: 100 },
          { id: "fc", label: "Fréquence cardiaque (/min)", type: "number", min: 40, max: 200 },
          { id: "pas", label: "PAS (mmHg)", type: "number", min: 60, max: 220 },
          { id: "creatinine", label: "Créatinine (mg/dL)", type: "number", min: 0.5, max: 10, step: 0.1 },
          { id: "killip", label: "Classe Killip", type: "select", options: [
            { value: 1, label: "I - Pas de signes d'IC" },
            { value: 2, label: "II - Râles, B3, turgescence jugulaire" },
            { value: 3, label: "III - OAP" },
            { value: 4, label: "IV - Choc cardiogénique" }
          ]},
          { id: "arret", label: "Arrêt cardiaque à l'admission", type: "boolean" },
          { id: "st", label: "Déviation du segment ST", type: "boolean" },
          { id: "troponine", label: "Élévation de la troponine", type: "boolean" }
        ],
        calculate: (values) => {
          let score = 0;
          // Age
          if (values.age < 40) score += 0;
          else if (values.age < 50) score += 18;
          else if (values.age < 60) score += 36;
          else if (values.age < 70) score += 55;
          else if (values.age < 80) score += 73;
          else score += 91;
          // FC
          if (values.fc < 70) score += 0;
          else if (values.fc < 90) score += 7;
          else if (values.fc < 110) score += 13;
          else if (values.fc < 150) score += 23;
          else score += 36;
          // PAS
          if (values.pas < 80) score += 63;
          else if (values.pas < 100) score += 58;
          else if (values.pas < 120) score += 47;
          else if (values.pas < 140) score += 37;
          else if (values.pas < 160) score += 26;
          else score += 11;
          // Créatinine
          if (values.creatinine < 1) score += 2;
          else if (values.creatinine < 2) score += 7;
          else if (values.creatinine < 3) score += 13;
          else score += 21;
          // Killip
          score += (values.killip - 1) * 21;
          // Autres
          if (values.arret) score += 43;
          if (values.st) score += 30;
          if (values.troponine) score += 15;
          return score;
        },
        interpretation: (score) => {
          if (score <= 108) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "Mortalité hospitalière <1%" };
          if (score <= 140) return { level: "modere", text: `Risque intermédiaire (${score})`, color: "text-yellow-400", action: "Mortalité hospitalière 1-3%" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Mortalité hospitalière >3%. Coronarographie urgente" };
        }
      }
    ]
  },

  // ============================================
  // 2. PNEUMOLOGIE
  // ============================================
  pneumologie: {
    name: "Pneumologie",
    icon: "Wind",
    color: "from-sky-600 to-blue-600",
    scores: [
      {
        id: "stopbang",
        name: "STOP-BANG",
        fullName: "Dépistage du SAOS",
        description: "Syndrome d'apnées obstructives du sommeil",
        cotation: { code: "GLQP007", libelle: "Polygraphie ventilatoire", tarif: 100.00 },
        fields: [
          { id: "snoring", label: "S - Ronflement fort (audible à travers une porte fermée)", type: "boolean", points: 1 },
          { id: "tired", label: "T - Fatigue diurne fréquente", type: "boolean", points: 1 },
          { id: "observed", label: "O - Apnées observées pendant le sommeil", type: "boolean", points: 1 },
          { id: "pressure", label: "P - HTA traitée", type: "boolean", points: 1 },
          { id: "bmi", label: "B - IMC > 35 kg/m²", type: "boolean", points: 1 },
          { id: "age50", label: "A - Âge > 50 ans", type: "boolean", points: 1 },
          { id: "neck", label: "N - Tour de cou > 40 cm", type: "boolean", points: 1 },
          { id: "gender", label: "G - Sexe masculin", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score <= 2) return { level: "faible", text: `Risque faible de SAOS (${score}/8)`, color: "text-green-400", action: "SAOS peu probable" };
          if (score <= 4) return { level: "modere", text: `Risque intermédiaire (${score}/8)`, color: "text-yellow-400", action: "Polygraphie ventilatoire à discuter" };
          return { level: "eleve", text: `Risque élevé de SAOS (${score}/8)`, color: "text-red-400", action: "Polygraphie/polysomnographie recommandée" };
        }
      },
      {
        id: "epworth",
        name: "Epworth",
        fullName: "Échelle de somnolence d'Epworth",
        description: "Évaluation de la somnolence diurne",
        cotation: null,
        questions: [
          "Assis en train de lire",
          "En regardant la télévision",
          "Assis inactif dans un lieu public",
          "Passager d'une voiture pendant 1h sans arrêt",
          "Allongé l'après-midi pour se reposer",
          "Assis en train de parler à quelqu'un",
          "Assis calmement après un repas sans alcool",
          "Dans une voiture immobilisée quelques minutes"
        ],
        options: [
          { label: "Ne somnolerait jamais", value: 0 },
          { label: "Faible chance", value: 1 },
          { label: "Chance moyenne", value: 2 },
          { label: "Forte chance", value: 3 }
        ],
        interpretation: (score) => {
          if (score <= 10) return { level: "normal", text: `Somnolence normale (${score}/24)`, color: "text-green-400", action: "Pas de somnolence pathologique" };
          if (score <= 15) return { level: "legere", text: `Somnolence légère (${score}/24)`, color: "text-yellow-400", action: "Rechercher cause: SAOS, dette de sommeil" };
          if (score <= 20) return { level: "moderee", text: `Somnolence modérée (${score}/24)`, color: "text-orange-400", action: "Bilan du sommeil recommandé" };
          return { level: "severe", text: `Somnolence sévère (${score}/24)`, color: "text-red-400", action: "Bilan urgent, attention conduite automobile" };
        }
      },
      {
        id: "mmrc",
        name: "mMRC",
        fullName: "Modified Medical Research Council",
        description: "Échelle de dyspnée (BPCO)",
        cotation: null,
        options: [
          { label: "Grade 0: Dyspnée uniquement pour un effort important", value: 0 },
          { label: "Grade 1: Essoufflé en marchant vite ou en montant une côte légère", value: 1 },
          { label: "Grade 2: Marche plus lentement que les gens du même âge ou doit s'arrêter pour reprendre son souffle", value: 2 },
          { label: "Grade 3: Doit s'arrêter après 100 mètres ou quelques minutes de marche", value: 3 },
          { label: "Grade 4: Trop essoufflé pour quitter la maison, ou dyspnée à l'habillage", value: 4 }
        ],
        interpretation: (score) => {
          if (score <= 1) return { level: "leger", text: `Dyspnée légère (mMRC ${score})`, color: "text-green-400", action: "BPCO groupe A/B - BDCA à la demande" };
          return { level: "severe", text: `Dyspnée significative (mMRC ${score})`, color: "text-red-400", action: "BPCO groupe B/E - LABA ± LAMA, réhabilitation" };
        }
      },
      {
        id: "cat",
        name: "CAT",
        fullName: "COPD Assessment Test",
        description: "Impact de la BPCO sur la qualité de vie",
        cotation: { code: "GLQP012", libelle: "Spirométrie", tarif: 40.28 },
        questions: [
          { text: "Je ne tousse jamais ↔ Je tousse en permanence", min: 0, max: 5 },
          { text: "Je n'ai pas de glaires ↔ Ma poitrine est pleine de glaires", min: 0, max: 5 },
          { text: "Je n'ai pas d'oppression ↔ J'ai une très forte oppression", min: 0, max: 5 },
          { text: "Pas d'essoufflement en montant ↔ Très essoufflé en montant", min: 0, max: 5 },
          { text: "Activités domestiques non limitées ↔ Très limitées", min: 0, max: 5 },
          { text: "Confiant pour sortir de chez moi ↔ Pas du tout confiant", min: 0, max: 5 },
          { text: "Je dors bien ↔ Je dors mal à cause de ma BPCO", min: 0, max: 5 },
          { text: "J'ai beaucoup d'énergie ↔ Je n'ai aucune énergie", min: 0, max: 5 }
        ],
        interpretation: (score) => {
          if (score < 10) return { level: "faible", text: `Impact faible (CAT ${score})`, color: "text-green-400", action: "BPCO groupe A - Traitement minimal" };
          if (score < 20) return { level: "modere", text: `Impact modéré (CAT ${score})`, color: "text-yellow-400", action: "BPCO groupe B - LABA ou LAMA" };
          if (score < 30) return { level: "eleve", text: `Impact élevé (CAT ${score})`, color: "text-orange-400", action: "BPCO groupe E - LABA+LAMA ± CSI" };
          return { level: "tres_eleve", text: `Impact très élevé (CAT ${score})`, color: "text-red-400", action: "Traitement maximal, réhabilitation, OLD si besoin" };
        }
      },
      {
        id: "act",
        name: "ACT",
        fullName: "Asthma Control Test",
        description: "Contrôle de l'asthme sur 4 semaines",
        cotation: null,
        questions: [
          { text: "Gêne au travail, école ou domicile due à l'asthme", options: [
            { label: "Tout le temps", value: 1 }, { label: "La plupart du temps", value: 2 }, { label: "Parfois", value: 3 }, { label: "Rarement", value: 4 }, { label: "Jamais", value: 5 }
          ]},
          { text: "Essoufflement", options: [
            { label: "Plus d'1 fois/jour", value: 1 }, { label: "1 fois/jour", value: 2 }, { label: "3-6 fois/sem", value: 3 }, { label: "1-2 fois/sem", value: 4 }, { label: "Jamais", value: 5 }
          ]},
          { text: "Réveils nocturnes dus à l'asthme", options: [
            { label: "≥4 nuits/sem", value: 1 }, { label: "2-3 nuits/sem", value: 2 }, { label: "1 nuit/sem", value: 3 }, { label: "1-2 fois/mois", value: 4 }, { label: "Jamais", value: 5 }
          ]},
          { text: "Utilisation du bronchodilatateur de secours", options: [
            { label: "≥3 fois/jour", value: 1 }, { label: "1-2 fois/jour", value: 2 }, { label: "2-3 fois/sem", value: 3 }, { label: "≤1 fois/sem", value: 4 }, { label: "Jamais", value: 5 }
          ]},
          { text: "Auto-évaluation du contrôle", options: [
            { label: "Pas du tout contrôlé", value: 1 }, { label: "Peu contrôlé", value: 2 }, { label: "Assez bien contrôlé", value: 3 }, { label: "Bien contrôlé", value: 4 }, { label: "Totalement contrôlé", value: 5 }
          ]}
        ],
        interpretation: (score) => {
          if (score >= 25) return { level: "controle", text: `Asthme bien contrôlé (${score}/25)`, color: "text-green-400", action: "Maintenir le traitement actuel" };
          if (score >= 20) return { level: "partiel", text: `Contrôle partiel (${score}/25)`, color: "text-yellow-400", action: "Envisager step-up thérapeutique" };
          return { level: "non_controle", text: `Asthme non contrôlé (${score}/25)`, color: "text-red-400", action: "Revoir l'observance, technique d'inhalation, step-up" };
        }
      },
      {
        id: "curb65",
        name: "CURB-65",
        fullName: "Confusion, Urea, Respiratory rate, Blood pressure, 65",
        description: "Gravité de la pneumonie communautaire (hospitalier)",
        cotation: null,
        fields: [
          { id: "confusion", label: "Confusion (désorienté temps/espace/personne)", type: "boolean", points: 1 },
          { id: "uree", label: "Urée > 7 mmol/L (ou créat > 110 µmol/L)", type: "boolean", points: 1 },
          { id: "fr", label: "Fréquence respiratoire ≥ 30/min", type: "boolean", points: 1 },
          { id: "ta", label: "TA: PAS < 90 ou PAD ≤ 60 mmHg", type: "boolean", points: 1 },
          { id: "age65", label: "Âge ≥ 65 ans", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score <= 1) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "Traitement ambulatoire possible. Mortalité <3%" };
          if (score === 2) return { level: "modere", text: `Risque intermédiaire (${score})`, color: "text-yellow-400", action: "Hospitalisation courte ou surveillance rapprochée. Mortalité 9%" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Hospitalisation, envisager réanimation si ≥4. Mortalité 15-40%" };
        }
      },
      {
        id: "crb65",
        name: "CRB-65",
        fullName: "Confusion, Respiratory rate, Blood pressure, 65",
        description: "Gravité de la pneumonie (ambulatoire, sans biologie)",
        cotation: null,
        fields: [
          { id: "confusion", label: "Confusion", type: "boolean", points: 1 },
          { id: "fr", label: "Fréquence respiratoire ≥ 30/min", type: "boolean", points: 1 },
          { id: "ta", label: "TA: PAS < 90 ou PAD ≤ 60 mmHg", type: "boolean", points: 1 },
          { id: "age65", label: "Âge ≥ 65 ans", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score === 0) return { level: "faible", text: "Risque faible (0)", color: "text-green-400", action: "Traitement ambulatoire" };
          if (score <= 2) return { level: "modere", text: `Risque intermédiaire (${score})`, color: "text-yellow-400", action: "Hospitalisation à considérer" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Hospitalisation urgente" };
        }
      }
    ]
  },

  // ============================================
  // 3. INFECTIOLOGIE / RÉANIMATION
  // ============================================
  infectiologie: {
    name: "Infectiologie / Réanimation",
    icon: "AlertTriangle",
    color: "from-red-700 to-red-600",
    scores: [
      {
        id: "qsofa",
        name: "qSOFA",
        fullName: "Quick Sequential Organ Failure Assessment",
        description: "Dépistage du sepsis hors réanimation",
        cotation: null,
        fields: [
          { id: "fr", label: "Fréquence respiratoire ≥ 22/min", type: "boolean", points: 1 },
          { id: "pas", label: "Pression artérielle systolique ≤ 100 mmHg", type: "boolean", points: 1 },
          { id: "glasgow", label: "Altération de la conscience (Glasgow < 15)", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score < 2) return { level: "negatif", text: `qSOFA négatif (${score}/3)`, color: "text-green-400", action: "Sepsis peu probable (rester vigilant)" };
          return { level: "positif", text: `qSOFA positif (${score}/3)`, color: "text-red-400", action: "SEPSIS PROBABLE - Hémocultures, lactates, antibiothérapie précoce, remplissage" };
        }
      },
      {
        id: "sofa",
        name: "SOFA",
        fullName: "Sequential Organ Failure Assessment",
        description: "Score de défaillance d'organes (réanimation)",
        cotation: null,
        fields: [
          { id: "pao2fio2", label: "PaO2/FiO2", type: "select", options: [
            { value: 0, label: "≥ 400" }, { value: 1, label: "300-399" }, { value: 2, label: "200-299" }, { value: 3, label: "100-199 + VM" }, { value: 4, label: "< 100 + VM" }
          ]},
          { id: "plaquettes", label: "Plaquettes (×10³/µL)", type: "select", options: [
            { value: 0, label: "≥ 150" }, { value: 1, label: "100-149" }, { value: 2, label: "50-99" }, { value: 3, label: "20-49" }, { value: 4, label: "< 20" }
          ]},
          { id: "bilirubine", label: "Bilirubine (mg/dL)", type: "select", options: [
            { value: 0, label: "< 1.2" }, { value: 1, label: "1.2-1.9" }, { value: 2, label: "2.0-5.9" }, { value: 3, label: "6.0-11.9" }, { value: 4, label: "≥ 12" }
          ]},
          { id: "cardiovasculaire", label: "Cardiovasculaire (PAM/catécholamines)", type: "select", options: [
            { value: 0, label: "PAM ≥ 70 mmHg" }, { value: 1, label: "PAM < 70" }, { value: 2, label: "Dopamine ≤5 ou Dobutamine" }, { value: 3, label: "Dopamine >5 ou NAd ≤0.1" }, { value: 4, label: "NAd >0.1 ou Adrénaline" }
          ]},
          { id: "glasgow", label: "Glasgow Coma Scale", type: "select", options: [
            { value: 0, label: "15" }, { value: 1, label: "13-14" }, { value: 2, label: "10-12" }, { value: 3, label: "6-9" }, { value: 4, label: "< 6" }
          ]},
          { id: "creatinine", label: "Créatinine (mg/dL) ou diurèse", type: "select", options: [
            { value: 0, label: "< 1.2" }, { value: 1, label: "1.2-1.9" }, { value: 2, label: "2.0-3.4" }, { value: 3, label: "3.5-4.9 ou diurèse <500mL/j" }, { value: 4, label: "≥ 5 ou diurèse <200mL/j" }
          ]}
        ],
        interpretation: (score) => {
          if (score < 2) return { level: "faible", text: `Défaillance minime (${score})`, color: "text-green-400", action: "Pas de sepsis selon SEPSIS-3" };
          if (score < 6) return { level: "modere", text: `Défaillance modérée (${score})`, color: "text-yellow-400", action: "SEPSIS si augmentation ≥2 points. Mortalité ~10%" };
          if (score < 10) return { level: "severe", text: `Défaillance sévère (${score})`, color: "text-orange-400", action: "Choc septique probable. Mortalité ~20-30%" };
          return { level: "critique", text: `Défaillance critique (${score})`, color: "text-red-600", action: "Mortalité >50%. Réanimation maximale" };
        }
      },
      {
        id: "news2",
        name: "NEWS2",
        fullName: "National Early Warning Score 2",
        description: "Score d'alerte précoce (détérioration clinique)",
        cotation: null,
        fields: [
          { id: "fr", label: "Fréquence respiratoire (/min)", type: "number", min: 5, max: 40 },
          { id: "spo2", label: "SpO2 (%)", type: "number", min: 70, max: 100 },
          { id: "o2", label: "Oxygénothérapie", type: "boolean" },
          { id: "pas", label: "PAS (mmHg)", type: "number", min: 50, max: 250 },
          { id: "fc", label: "Fréquence cardiaque (/min)", type: "number", min: 30, max: 200 },
          { id: "conscience", label: "Conscience", type: "select", options: [
            { value: "A", label: "A - Alerte" }, { value: "C", label: "C - Confusion nouvelle" }, { value: "V", label: "V - Répond à la voix" }, { value: "P", label: "P - Répond à la douleur" }, { value: "U", label: "U - Inconscient" }
          ]},
          { id: "temp", label: "Température (°C)", type: "number", min: 34, max: 42, step: 0.1 }
        ],
        calculate: (values) => {
          let score = 0;
          // FR
          if (values.fr <= 8) score += 3;
          else if (values.fr <= 11) score += 1;
          else if (values.fr <= 20) score += 0;
          else if (values.fr <= 24) score += 2;
          else score += 3;
          // SpO2
          if (values.spo2 <= 91) score += 3;
          else if (values.spo2 <= 93) score += 2;
          else if (values.spo2 <= 95) score += 1;
          // O2
          if (values.o2) score += 2;
          // PAS
          if (values.pas <= 90) score += 3;
          else if (values.pas <= 100) score += 2;
          else if (values.pas <= 110) score += 1;
          else if (values.pas >= 220) score += 3;
          // FC
          if (values.fc <= 40) score += 3;
          else if (values.fc <= 50) score += 1;
          else if (values.fc <= 90) score += 0;
          else if (values.fc <= 110) score += 1;
          else if (values.fc <= 130) score += 2;
          else score += 3;
          // Conscience
          if (values.conscience !== "A") score += 3;
          // Temp
          if (values.temp <= 35) score += 3;
          else if (values.temp <= 36) score += 1;
          else if (values.temp <= 38) score += 0;
          else if (values.temp <= 39) score += 1;
          else score += 2;
          return score;
        },
        interpretation: (score) => {
          if (score <= 4) return { level: "faible", text: `Risque faible (NEWS2: ${score})`, color: "text-green-400", action: "Surveillance standard (toutes les 12h)" };
          if (score <= 6) return { level: "modere", text: `Risque modéré (NEWS2: ${score})`, color: "text-yellow-400", action: "Surveillance rapprochée (toutes les 4-6h), avis médical" };
          if (score === 7) return { level: "eleve", text: `Risque élevé (NEWS2: ${score})`, color: "text-orange-400", action: "Surveillance horaire, avis médical URGENT" };
          return { level: "critique", text: `CRITIQUE (NEWS2: ${score})`, color: "text-red-600", action: "APPEL RÉANIMATION, surveillance continue" };
        }
      }
    ]
  },

  // ============================================
  // 4. NEUROLOGIE
  // ============================================
  neurologie: {
    name: "Neurologie",
    icon: "Brain",
    color: "from-purple-600 to-indigo-600",
    scores: [
      {
        id: "nihss",
        name: "NIHSS",
        fullName: "National Institutes of Health Stroke Scale",
        description: "Gravité de l'AVC ischémique",
        cotation: { code: "ANRP001", libelle: "Examen neurologique", tarif: 46.00 },
        sections: [
          { name: "1a. Niveau de conscience", options: [
            { label: "Vigilant", value: 0 }, { label: "Non vigilant mais réveillable", value: 1 }, { label: "Non vigilant, réponse à stimulation", value: 2 }, { label: "Coma", value: 3 }
          ]},
          { name: "1b. Questions (mois, âge)", options: [
            { label: "2 réponses correctes", value: 0 }, { label: "1 réponse correcte", value: 1 }, { label: "0 réponse correcte", value: 2 }
          ]},
          { name: "1c. Ordres (fermer yeux, serrer main)", options: [
            { label: "2 ordres corrects", value: 0 }, { label: "1 ordre correct", value: 1 }, { label: "0 ordre correct", value: 2 }
          ]},
          { name: "2. Oculomotricité", options: [
            { label: "Normal", value: 0 }, { label: "Paralysie partielle", value: 1 }, { label: "Déviation forcée", value: 2 }
          ]},
          { name: "3. Champ visuel", options: [
            { label: "Normal", value: 0 }, { label: "Hémianopsie partielle", value: 1 }, { label: "Hémianopsie complète", value: 2 }, { label: "Cécité bilatérale", value: 3 }
          ]},
          { name: "4. Paralysie faciale", options: [
            { label: "Absente", value: 0 }, { label: "Mineure", value: 1 }, { label: "Partielle", value: 2 }, { label: "Complète", value: 3 }
          ]},
          { name: "5a. Motricité MS gauche", options: [
            { label: "Pas de chute", value: 0 }, { label: "Chute après 10s", value: 1 }, { label: "Chute < 10s", value: 2 }, { label: "Ébauche de mouvement", value: 3 }, { label: "Aucun mouvement", value: 4 }
          ]},
          { name: "5b. Motricité MS droit", options: [
            { label: "Pas de chute", value: 0 }, { label: "Chute après 10s", value: 1 }, { label: "Chute < 10s", value: 2 }, { label: "Ébauche de mouvement", value: 3 }, { label: "Aucun mouvement", value: 4 }
          ]},
          { name: "6a. Motricité MI gauche", options: [
            { label: "Pas de chute", value: 0 }, { label: "Chute après 5s", value: 1 }, { label: "Chute < 5s", value: 2 }, { label: "Ébauche de mouvement", value: 3 }, { label: "Aucun mouvement", value: 4 }
          ]},
          { name: "6b. Motricité MI droit", options: [
            { label: "Pas de chute", value: 0 }, { label: "Chute après 5s", value: 1 }, { label: "Chute < 5s", value: 2 }, { label: "Ébauche de mouvement", value: 3 }, { label: "Aucun mouvement", value: 4 }
          ]},
          { name: "7. Ataxie des membres", options: [
            { label: "Absente", value: 0 }, { label: "1 membre", value: 1 }, { label: "2 membres", value: 2 }
          ]},
          { name: "8. Sensibilité", options: [
            { label: "Normale", value: 0 }, { label: "Hypoesthésie légère/modérée", value: 1 }, { label: "Hypoesthésie sévère/anesthésie", value: 2 }
          ]},
          { name: "9. Langage", options: [
            { label: "Normal", value: 0 }, { label: "Aphasie légère/modérée", value: 1 }, { label: "Aphasie sévère", value: 2 }, { label: "Mutisme/aphasie globale", value: 3 }
          ]},
          { name: "10. Dysarthrie", options: [
            { label: "Absente", value: 0 }, { label: "Légère à modérée", value: 1 }, { label: "Sévère/anarthrie", value: 2 }
          ]},
          { name: "11. Extinction/négligence", options: [
            { label: "Absente", value: 0 }, { label: "Partielle (1 modalité)", value: 1 }, { label: "Complète (2 modalités)", value: 2 }
          ]}
        ],
        interpretation: (score) => {
          if (score === 0) return { level: "absent", text: "Pas de déficit (0)", color: "text-green-400", action: "AIT ou AVC mineur résolu" };
          if (score <= 4) return { level: "mineur", text: `AVC mineur (${score})`, color: "text-yellow-400", action: "Thrombolyse si < 4.5h et indication" };
          if (score <= 15) return { level: "modere", text: `AVC modéré (${score})`, color: "text-orange-400", action: "Thrombolyse IV ± thrombectomie si occlusion proximale" };
          if (score <= 20) return { level: "severe", text: `AVC sévère (${score})`, color: "text-red-400", action: "Thrombectomie si occlusion proximale" };
          return { level: "tres_severe", text: `AVC très sévère (${score})`, color: "text-red-600", action: "Pronostic réservé, discuter réanimation" };
        }
      },
      {
        id: "glasgow",
        name: "Glasgow",
        fullName: "Glasgow Coma Scale",
        description: "Évaluation du niveau de conscience",
        cotation: null,
        sections: [
          { name: "Ouverture des yeux (E)", options: [
            { label: "Spontanée", value: 4 }, { label: "À la demande", value: 3 }, { label: "À la douleur", value: 2 }, { label: "Aucune", value: 1 }
          ]},
          { name: "Réponse verbale (V)", options: [
            { label: "Orientée", value: 5 }, { label: "Confuse", value: 4 }, { label: "Mots inappropriés", value: 3 }, { label: "Sons incompréhensibles", value: 2 }, { label: "Aucune", value: 1 }
          ]},
          { name: "Réponse motrice (M)", options: [
            { label: "Obéit aux ordres", value: 6 }, { label: "Localise la douleur", value: 5 }, { label: "Évitement", value: 4 }, { label: "Flexion anormale (décortication)", value: 3 }, { label: "Extension (décérébration)", value: 2 }, { label: "Aucune", value: 1 }
          ]}
        ],
        interpretation: (score) => {
          if (score === 15) return { level: "normal", text: "Conscience normale (15)", color: "text-green-400", action: "Pas de trouble de conscience" };
          if (score >= 13) return { level: "leger", text: `Altération légère (${score})`, color: "text-yellow-400", action: "Surveillance, rechercher cause" };
          if (score >= 9) return { level: "modere", text: `Altération modérée (${score})`, color: "text-orange-400", action: "Scanner cérébral, avis spécialisé" };
          if (score >= 6) return { level: "severe", text: `Coma (${score})`, color: "text-red-400", action: "Réanimation, protection voies aériennes" };
          return { level: "tres_severe", text: `Coma profond (${score})`, color: "text-red-600", action: "Intubation, réanimation, pronostic réservé" };
        }
      },
      {
        id: "abcd2",
        name: "ABCD²",
        fullName: "Age, Blood pressure, Clinical features, Duration, Diabetes",
        description: "Risque d'AVC après un AIT",
        cotation: { code: "ACQK001", libelle: "IRM cérébrale", tarif: 69.00 },
        fields: [
          { id: "age60", label: "Âge ≥ 60 ans", type: "boolean", points: 1 },
          { id: "ta", label: "TA ≥ 140/90 mmHg à l'admission", type: "boolean", points: 1 },
          { id: "deficit_moteur", label: "Déficit moteur unilatéral", type: "boolean", points: 2 },
          { id: "trouble_langage", label: "Trouble du langage sans déficit moteur", type: "boolean", points: 1 },
          { id: "duree60", label: "Durée ≥ 60 minutes", type: "boolean", points: 2 },
          { id: "duree10", label: "Durée 10-59 minutes", type: "boolean", points: 1 },
          { id: "diabete", label: "Diabète", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score <= 3) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "Risque d'AVC à 2 jours: 1%. Bilan en externe possible" };
          if (score <= 5) return { level: "modere", text: `Risque modéré (${score})`, color: "text-yellow-400", action: "Risque d'AVC à 2 jours: 4%. Hospitalisation recommandée" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Risque d'AVC à 2 jours: 8%. Hospitalisation urgente, IRM/ARM" };
        }
      },
      {
        id: "rankin",
        name: "Rankin modifié (mRS)",
        fullName: "Modified Rankin Scale",
        description: "Handicap fonctionnel post-AVC",
        cotation: null,
        options: [
          { label: "0 - Aucun symptôme", value: 0 },
          { label: "1 - Pas de handicap significatif malgré symptômes", value: 1 },
          { label: "2 - Handicap léger, autonome mais limitation activités", value: 2 },
          { label: "3 - Handicap modéré, marche seul mais aide pour certaines activités", value: 3 },
          { label: "4 - Handicap modérément sévère, ne peut marcher sans aide, assistance pour besoins", value: 4 },
          { label: "5 - Handicap sévère, alité, incontinent, soins constants", value: 5 },
          { label: "6 - Décès", value: 6 }
        ],
        interpretation: (score) => {
          if (score <= 1) return { level: "bon", text: `Évolution favorable (mRS ${score})`, color: "text-green-400", action: "Autonomie préservée" };
          if (score <= 2) return { level: "acceptable", text: `Handicap léger (mRS ${score})`, color: "text-yellow-400", action: "Rééducation, adaptation" };
          if (score <= 4) return { level: "modere", text: `Handicap significatif (mRS ${score})`, color: "text-orange-400", action: "Aides à domicile, rééducation intensive" };
          return { level: "severe", text: `Handicap sévère (mRS ${score})`, color: "text-red-400", action: "Soins institutionnels ou palliatifs" };
        }
      }
    ]
  },

  // ============================================
  // 5. GÉRIATRIE
  // ============================================
  geriatrie: {
    name: "Gériatrie",
    icon: "Users",
    color: "from-amber-600 to-orange-600",
    scores: [
      {
        id: "mmse",
        name: "MMSE",
        fullName: "Mini Mental State Examination",
        description: "Évaluation cognitive globale",
        cotation: { code: "ALQP006", libelle: "Test psychométrique", tarif: 69.12 },
        sections: [
          { name: "Orientation temporelle (/5)", max: 5, items: ["Année", "Saison", "Mois", "Jour du mois", "Jour de la semaine"] },
          { name: "Orientation spatiale (/5)", max: 5, items: ["Pays", "Région", "Ville", "Lieu (hôpital)", "Étage"] },
          { name: "Apprentissage 3 mots (/3)", max: 3, items: ["Cigare", "Fleur", "Porte"] },
          { name: "Attention et calcul (/5)", max: 5, items: ["100-7 (93-86-79-72-65)"] },
          { name: "Rappel des 3 mots (/3)", max: 3 },
          { name: "Langage - Dénomination (/2)", max: 2, items: ["Crayon", "Montre"] },
          { name: "Langage - Répétition (/1)", max: 1, items: ["Pas de si, ni de mais"] },
          { name: "Langage - Ordre en 3 temps (/3)", max: 3, items: ["Prendre la feuille", "Plier en deux", "Jeter à terre"] },
          { name: "Langage - Lecture/exécution (/1)", max: 1, items: ["Fermez les yeux"] },
          { name: "Langage - Écriture phrase (/1)", max: 1 },
          { name: "Praxies constructives (/1)", max: 1, items: ["Copie pentagones"] }
        ],
        interpretation: (score) => {
          if (score >= 27) return { level: "normal", text: `Normal (${score}/30)`, color: "text-green-400", action: "Pas de trouble cognitif majeur" };
          if (score >= 24) return { level: "leger", text: `Trouble léger (${score}/30)`, color: "text-yellow-400", action: "MCI possible, surveillance, tests complémentaires" };
          if (score >= 19) return { level: "modere", text: `Démence légère (${score}/30)`, color: "text-orange-400", action: "Bilan étiologique, IAChE à discuter" };
          if (score >= 10) return { level: "modere_severe", text: `Démence modérée (${score}/30)`, color: "text-red-400", action: "Prise en charge spécialisée, protection juridique" };
          return { level: "severe", text: `Démence sévère (${score}/30)`, color: "text-red-600", action: "Soins adaptés, protection, accompagnement aidants" };
        }
      },
      {
        id: "moca",
        name: "MoCA",
        fullName: "Montreal Cognitive Assessment",
        description: "Détection des troubles cognitifs légers (MCI)",
        cotation: { code: "ALQP006", libelle: "Test psychométrique", tarif: 69.12 },
        sections: [
          { name: "Visuospatial/Exécutif (/5)", max: 5, items: ["Trail Making B adapté", "Cube", "Horloge"] },
          { name: "Dénomination (/3)", max: 3, items: ["Lion", "Rhinocéros", "Chameau"] },
          { name: "Mémoire - Apprentissage", max: 0, items: ["2 essais de 5 mots (pas de points)"] },
          { name: "Attention (/6)", max: 6, items: ["Empan endroit", "Empan envers", "Vigilance", "Calcul mental"] },
          { name: "Langage (/3)", max: 3, items: ["Répétition 2 phrases", "Fluence lettre P"] },
          { name: "Abstraction (/2)", max: 2, items: ["Similitudes: train/vélo, montre/règle"] },
          { name: "Rappel différé (/5)", max: 5, items: ["Rappel libre des 5 mots"] },
          { name: "Orientation (/6)", max: 6, items: ["Date", "Mois", "Année", "Jour", "Lieu", "Ville"] }
        ],
        interpretation: (score) => {
          if (score >= 26) return { level: "normal", text: `Normal (${score}/30)`, color: "text-green-400", action: "Pas de MCI détecté" };
          if (score >= 22) return { level: "mci", text: `MCI probable (${score}/30)`, color: "text-yellow-400", action: "Bilan neuropsychologique complet" };
          if (score >= 17) return { level: "demence_legere", text: `Démence légère probable (${score}/30)`, color: "text-orange-400", action: "Bilan étiologique, IRM, prise en charge" };
          return { level: "demence", text: `Démence (${score}/30)`, color: "text-red-400", action: "Prise en charge spécialisée" };
        }
      },
      {
        id: "adl",
        name: "ADL (Katz)",
        fullName: "Activities of Daily Living",
        description: "Autonomie pour les activités de base",
        cotation: { code: "ALQP009", libelle: "Évaluation gériatrique", tarif: 69.12 },
        activities: [
          { name: "Hygiène corporelle", options: [{ label: "Autonome", value: 1 }, { label: "Aide partielle ou totale", value: 0 }] },
          { name: "Habillage", options: [{ label: "Autonome", value: 1 }, { label: "Aide partielle ou totale", value: 0 }] },
          { name: "Aller aux toilettes", options: [{ label: "Autonome", value: 1 }, { label: "Aide partielle ou totale", value: 0 }] },
          { name: "Locomotion", options: [{ label: "Autonome", value: 1 }, { label: "Aide partielle ou totale", value: 0 }] },
          { name: "Continence", options: [{ label: "Continent", value: 1 }, { label: "Accidents ou incontinent", value: 0 }] },
          { name: "Alimentation", options: [{ label: "Autonome", value: 1 }, { label: "Aide partielle ou totale", value: 0 }] }
        ],
        interpretation: (score) => {
          if (score === 6) return { level: "autonome", text: `Autonomie complète (${score}/6)`, color: "text-green-400", action: "Maintien à domicile sans aide" };
          if (score >= 4) return { level: "dependance_legere", text: `Dépendance légère (${score}/6)`, color: "text-yellow-400", action: "Aide ponctuelle" };
          if (score >= 2) return { level: "dependance_moderee", text: `Dépendance modérée (${score}/6)`, color: "text-orange-400", action: "Aide quotidienne, APA" };
          return { level: "dependance_severe", text: `Dépendance sévère (${score}/6)`, color: "text-red-400", action: "Aide permanente, EHPAD à discuter" };
        }
      },
      {
        id: "iadl",
        name: "IADL (Lawton)",
        fullName: "Instrumental Activities of Daily Living",
        description: "Activités instrumentales de la vie quotidienne",
        cotation: { code: "ALQP009", libelle: "Évaluation gériatrique", tarif: 69.12 },
        activities: [
          { name: "Téléphone", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] },
          { name: "Courses", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] },
          { name: "Préparation des repas", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] },
          { name: "Entretien ménager", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] },
          { name: "Lessive", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] },
          { name: "Transports", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] },
          { name: "Médicaments", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] },
          { name: "Finances", options: [{ label: "Autonome", value: 1 }, { label: "Aide nécessaire", value: 0 }] }
        ],
        interpretation: (score) => {
          if (score === 8) return { level: "autonome", text: `Autonomie complète (${score}/8)`, color: "text-green-400", action: "Indépendance pour les activités instrumentales" };
          if (score >= 5) return { level: "legere", text: `Dépendance légère (${score}/8)`, color: "text-yellow-400", action: "Aide ponctuelle recommandée" };
          if (score >= 2) return { level: "moderee", text: `Dépendance modérée (${score}/8)`, color: "text-orange-400", action: "Aide régulière, évaluation cognitive" };
          return { level: "severe", text: `Dépendance sévère (${score}/8)`, color: "text-red-400", action: "Aide permanente nécessaire" };
        }
      },
      {
        id: "mna_sf",
        name: "MNA-SF",
        fullName: "Mini Nutritional Assessment - Short Form",
        description: "Dépistage de la dénutrition du sujet âgé",
        cotation: { code: "ALQP009", libelle: "Évaluation gériatrique", tarif: 69.12 },
        questions: [
          { text: "Diminution des apports alimentaires (3 derniers mois)", options: [
            { label: "Anorexie sévère", value: 0 }, { label: "Anorexie modérée", value: 1 }, { label: "Pas d'anorexie", value: 2 }
          ]},
          { text: "Perte de poids (3 derniers mois)", options: [
            { label: "> 3 kg", value: 0 }, { label: "Ne sait pas", value: 1 }, { label: "1-3 kg", value: 2 }, { label: "Pas de perte", value: 3 }
          ]},
          { text: "Mobilité", options: [
            { label: "Lit/fauteuil", value: 0 }, { label: "Autonome à l'intérieur", value: 1 }, { label: "Sort du domicile", value: 2 }
          ]},
          { text: "Stress psychologique ou maladie aiguë (3 derniers mois)", options: [
            { label: "Oui", value: 0 }, { label: "Non", value: 2 }
          ]},
          { text: "Problèmes neuropsychologiques", options: [
            { label: "Démence ou dépression sévère", value: 0 }, { label: "Démence légère", value: 1 }, { label: "Pas de problème", value: 2 }
          ]},
          { text: "IMC", options: [
            { label: "< 19", value: 0 }, { label: "19-21", value: 1 }, { label: "21-23", value: 2 }, { label: "≥ 23", value: 3 }
          ]}
        ],
        interpretation: (score) => {
          if (score >= 12) return { level: "normal", text: `État nutritionnel normal (${score}/14)`, color: "text-green-400", action: "Pas de dénutrition" };
          if (score >= 8) return { level: "risque", text: `Risque de dénutrition (${score}/14)`, color: "text-yellow-400", action: "MNA complet, enrichissement alimentaire" };
          return { level: "denutrition", text: `Dénutrition probable (${score}/14)`, color: "text-red-400", action: "CNO, avis diététicien, rechercher cause" };
        }
      },
      {
        id: "cfs",
        name: "Clinical Frailty Scale",
        fullName: "Échelle de fragilité clinique",
        description: "Évaluation de la fragilité du sujet âgé",
        cotation: null,
        options: [
          { label: "1 - Très en forme (exercice régulier, au-dessus de la moyenne)", value: 1 },
          { label: "2 - En forme (exercice occasionnel)", value: 2 },
          { label: "3 - Se débrouille bien (pas d'exercice régulier mais actif)", value: 3 },
          { label: "4 - Vulnérable (ralenti, symptômes limitant les activités)", value: 4 },
          { label: "5 - Légèrement fragile (aide pour IADL)", value: 5 },
          { label: "6 - Modérément fragile (aide pour IADL et ADL)", value: 6 },
          { label: "7 - Sévèrement fragile (dépendance totale)", value: 7 },
          { label: "8 - Très sévèrement fragile (fin de vie approche)", value: 8 },
          { label: "9 - En phase terminale", value: 9 }
        ],
        interpretation: (score) => {
          if (score <= 3) return { level: "non_fragile", text: `Non fragile (CFS ${score})`, color: "text-green-400", action: "Pas de fragilité, prévention" };
          if (score === 4) return { level: "vulnerable", text: `Vulnérable (CFS ${score})`, color: "text-yellow-400", action: "Pré-fragilité, intervention préventive" };
          if (score <= 6) return { level: "fragile", text: `Fragile (CFS ${score})`, color: "text-orange-400", action: "EGS, plan personnalisé de soins" };
          return { level: "tres_fragile", text: `Très fragile (CFS ${score})`, color: "text-red-400", action: "Soins adaptés, discussion directives anticipées" };
        }
      }
    ]
  },

  // ============================================
  // 6. PSYCHIATRIE / ADDICTOLOGIE
  // ============================================
  psychiatrie: {
    name: "Psychiatrie / Addictologie",
    icon: "Heart",
    color: "from-purple-600 to-pink-600",
    scores: [
      {
        id: "phq9",
        name: "PHQ-9",
        fullName: "Patient Health Questionnaire-9",
        description: "Dépistage et suivi de la dépression",
        cotation: { code: "ALQP003", libelle: "Évaluation psychiatrique", tarif: 69.12 },
        questions: [
          "Peu d'intérêt ou de plaisir à faire les choses",
          "Se sentir triste, déprimé(e) ou désespéré(e)",
          "Difficultés à s'endormir, rester endormi(e), ou trop dormir",
          "Se sentir fatigué(e) ou manquer d'énergie",
          "Peu d'appétit ou manger trop",
          "Mauvaise opinion de soi-même, sentiment d'échec",
          "Difficultés à se concentrer",
          "Bouger ou parler lentement / Être agité(e)",
          "Pensées de se faire du mal ou d'être mieux mort(e)"
        ],
        options: [
          { label: "Jamais", value: 0 },
          { label: "Plusieurs jours", value: 1 },
          { label: "Plus de la moitié du temps", value: 2 },
          { label: "Presque tous les jours", value: 3 }
        ],
        interpretation: (score) => {
          if (score <= 4) return { level: "minimal", text: `Dépression minimale (${score})`, color: "text-green-400", action: "Pas de traitement spécifique" };
          if (score <= 9) return { level: "leger", text: `Dépression légère (${score})`, color: "text-yellow-400", action: "Surveillance, psychoéducation, réévaluation 2 semaines" };
          if (score <= 14) return { level: "modere", text: `Dépression modérée (${score})`, color: "text-orange-400", action: "Psychothérapie ± antidépresseur" };
          if (score <= 19) return { level: "modere_severe", text: `Dépression modérément sévère (${score})`, color: "text-red-400", action: "Antidépresseur + psychothérapie" };
          return { level: "severe", text: `Dépression sévère (${score})`, color: "text-red-600", action: "URGENT: évaluer risque suicidaire, traitement actif, hospitalisation?" };
        }
      },
      {
        id: "hamilton",
        name: "Hamilton (HDRS)",
        fullName: "Échelle de dépression de Hamilton",
        description: "Évaluation de l'intensité des symptômes dépressifs (17 items)",
        source: "MG France - Cotation ALQP003 (1 fois/an)",
        cotation: { code: "ALQP003", libelle: "Test de dépression structuré", tarif: 69.12 },
        hamiltonQuestions: [
          {
            id: "humeur",
            label: "1. Humeur dépressive",
            description: "Tristesse, impuissance, auto-dépréciation",
            options: [
              { label: "Non", value: 0 },
              { label: "États affectifs signalés uniquement si on l'interroge", value: 1 },
              { label: "États signalés spontanément (verbal, sanglots occasionnels)", value: 2 },
              { label: "États communiqués non verbalement (expression, attitude)", value: 3 },
              { label: "Ne communique pratiquement que ces états affectifs", value: 4 }
            ]
          },
          {
            id: "culpabilite",
            label: "2. Sentiments de culpabilité",
            description: "Reproches, ruminations, idées de punition",
            options: [
              { label: "N'a pas de sentiments de culpabilité", value: 0 },
              { label: "S'adresse des reproches, impression d'avoir porté préjudice", value: 1 },
              { label: "Idées de culpabilité, rumination sur erreurs passées", value: 2 },
              { label: "La maladie actuelle est une punition. Idées délirantes", value: 3 },
              { label: "Hallucinations accusatrices ou menaçantes", value: 4 }
            ]
          },
          {
            id: "suicide",
            label: "3. Suicide",
            description: "Idées et gestes suicidaires",
            options: [
              { label: "N'a pas d'idée suicidaire", value: 0 },
              { label: "Impression que la vie ne vaut pas la peine d'être vécue", value: 1 },
              { label: "Souhaite être mort, pensées de mort dirigées contre lui-même", value: 2 },
              { label: "Idées ou geste de suicide", value: 3 },
              { label: "Tentatives de suicide sérieuses", value: 4 }
            ]
          },
          {
            id: "insomnie_debut",
            label: "4. Insomnie de début de nuit",
            description: "Difficultés d'endormissement",
            options: [
              { label: "Pas de difficulté à s'endormir", value: 0 },
              { label: "Difficultés éventuelles à s'endormir", value: 1 },
              { label: "Difficultés chaque soir à s'endormir", value: 2 }
            ]
          },
          {
            id: "insomnie_milieu",
            label: "5. Insomnie en milieu de nuit",
            description: "Réveils nocturnes",
            options: [
              { label: "Pas de difficulté", value: 0 },
              { label: "Agité ou troublé pendant la nuit", value: 1 },
              { label: "Réveils pendant la nuit (se lève)", value: 2 }
            ]
          },
          {
            id: "insomnie_matin",
            label: "6. Insomnie du matin",
            description: "Réveil précoce",
            options: [
              { label: "Pas de difficulté", value: 0 },
              { label: "Se réveille de très bonne heure mais se rendort", value: 1 },
              { label: "Incapable de se rendormir s'il se lève", value: 2 }
            ]
          },
          {
            id: "travail",
            label: "7. Travail et activités",
            description: "Activités professionnelles et de détente",
            options: [
              { label: "Pas de difficulté", value: 0 },
              { label: "Pensées d'incapacité, fatigue, faiblesse", value: 1 },
              { label: "Perte d'intérêt, apathie, indécision", value: 2 },
              { label: "Diminution du temps d'activité ou productivité", value: 3 },
              { label: "A arrêté son travail en raison de sa maladie", value: 4 }
            ]
          },
          {
            id: "ralentissement",
            label: "8. Ralentissement",
            description: "Lenteur pensée/langage, concentration, activité motrice",
            options: [
              { label: "Pensée et langage normaux", value: 0 },
              { label: "Léger ralentissement à l'entretien", value: 1 },
              { label: "Ralentissement manifeste lors de l'entretien", value: 2 },
              { label: "Entretien difficile", value: 3 },
              { label: "Entretien impossible (état de stupeur)", value: 4 }
            ]
          },
          {
            id: "agitation",
            label: "9. Agitation",
            description: "Agitation psychomotrice",
            options: [
              { label: "Aucune", value: 0 },
              { label: "Crispations, secousses musculaires", value: 1 },
              { label: "Joue avec ses mains, cheveux...", value: 2 },
              { label: "Bouge, ne peut rester assis tranquille", value: 3 },
              { label: "Se tord les mains, se ronge les ongles...", value: 4 }
            ]
          },
          {
            id: "anxiete_psychique",
            label: "10. Anxiété psychique",
            description: "Tension, inquiétude, peurs",
            options: [
              { label: "Aucune", value: 0 },
              { label: "Symptômes légers - Tension subjective, irritabilité", value: 1 },
              { label: "Symptômes modérés - Souci pour problèmes mineurs", value: 2 },
              { label: "Symptômes sévères - Attitude inquiète apparente", value: 3 },
              { label: "Symptômes très invalidants - Peurs spontanément exprimées", value: 4 }
            ]
          },
          {
            id: "anxiete_somatique",
            label: "11. Anxiété somatique",
            description: "Bouche sèche, troubles digestifs, palpitations, céphalées...",
            options: [
              { label: "Aucun symptôme", value: 0 },
              { label: "Symptômes légers", value: 1 },
              { label: "Symptômes modérés", value: 2 },
              { label: "Symptômes sévères", value: 3 },
              { label: "Symptômes très invalidants (incapacité fonctionnelle)", value: 4 }
            ]
          },
          {
            id: "gastro_intestinaux",
            label: "12. Symptômes somatiques gastro-intestinaux",
            description: "Appétit, troubles digestifs",
            options: [
              { label: "Aucun symptôme", value: 0 },
              { label: "Manque d'appétit mais mange sans y être poussé", value: 1 },
              { label: "Difficultés à manger, besoins de laxatifs", value: 2 }
            ]
          },
          {
            id: "somatiques_generaux",
            label: "13. Symptômes somatiques généraux",
            description: "Fatigue, douleurs, lourdeur",
            options: [
              { label: "Aucun", value: 0 },
              { label: "Lourdeur membres/dos/tête, douleurs, fatigabilité", value: 1 },
              { label: "Un des symptômes apparaît clairement", value: 2 }
            ]
          },
          {
            id: "genitaux",
            label: "14. Symptômes génitaux",
            description: "Perte de libido, troubles menstruels",
            options: [
              { label: "Absents", value: 0 },
              { label: "Légers", value: 1 },
              { label: "Sévères", value: 2 }
            ]
          },
          {
            id: "hypochondrie",
            label: "15. Hypochondrie",
            description: "Préoccupations corporelles",
            options: [
              { label: "Absente", value: 0 },
              { label: "Attention concentrée sur son propre corps", value: 1 },
              { label: "Préoccupations sur sa santé", value: 2 },
              { label: "Plaintes fréquentes, convaincu d'être malade", value: 3 },
              { label: "Idées délirantes hypochondriaques", value: 4 }
            ]
          },
          {
            id: "perte_poids",
            label: "16. Perte de poids",
            description: "Perte de poids récente",
            options: [
              { label: "Pas de perte de poids", value: 0 },
              { label: "Perte de poids probable", value: 1 },
              { label: "Perte de poids certaine", value: 2 }
            ]
          },
          {
            id: "conscience",
            label: "17. Prise de conscience",
            description: "Reconnaissance de la maladie",
            options: [
              { label: "Reconnaît être déprimé et malade", value: 0 },
              { label: "Reconnaît être malade mais l'attribue à autre chose", value: 1 },
              { label: "Nie être malade", value: 2 }
            ]
          }
        ],
        interpretation: (score) => {
          if (score < 10) return { level: "normal", text: `Pas de dépression (${score})`, color: "text-green-400", action: "Pas de symptômes dépressifs significatifs" };
          if (score <= 13) return { level: "leger", text: `Dépression légère (${score})`, color: "text-yellow-400", action: "Surveillance, psychoéducation, revoir dans 2-4 semaines" };
          if (score <= 17) return { level: "modere", text: `Dépression légère à modérée (${score})`, color: "text-orange-400", action: "Psychothérapie recommandée ± antidépresseur" };
          return { level: "severe", text: `Dépression modérée à sévère (${score})`, color: "text-red-400", action: "Traitement antidépresseur + psychothérapie, évaluer risque suicidaire" };
        }
      },
      {
        id: "bdi2",
        name: "BDI-II (Beck)",
        fullName: "Inventaire de Dépression de Beck - 2ème édition",
        description: "Auto-questionnaire de dépistage et suivi de la dépression (21 items)",
        source: "Beck AT, 1996 - Questionnaire validé en français",
        cotation: { code: "ALQP003", libelle: "Test de dépression structuré", tarif: 69.12 },
        beckQuestions: [
          {
            id: "tristesse",
            label: "1. Tristesse",
            options: [
              { label: "Je ne me sens pas triste", value: 0 },
              { label: "Je me sens souvent triste", value: 1 },
              { label: "Je suis tout le temps triste", value: 2 },
              { label: "Je suis si triste ou malheureux(se) que ce n'est pas supportable", value: 3 }
            ]
          },
          {
            id: "pessimisme",
            label: "2. Pessimisme",
            options: [
              { label: "Je ne suis pas découragé(e) face à mon avenir", value: 0 },
              { label: "Je me sens plus découragé(e) qu'avant face à mon avenir", value: 1 },
              { label: "Je ne m'attends pas à ce que les choses s'arrangent pour moi", value: 2 },
              { label: "J'ai le sentiment que mon avenir est sans espoir et ne peut qu'empirer", value: 3 }
            ]
          },
          {
            id: "echecs_passes",
            label: "3. Échecs dans le passé",
            options: [
              { label: "Je n'ai pas le sentiment d'avoir échoué dans la vie", value: 0 },
              { label: "J'ai échoué plus souvent que je n'aurais dû", value: 1 },
              { label: "Quand je regarde en arrière, je vois beaucoup d'échecs", value: 2 },
              { label: "J'ai le sentiment d'avoir complètement raté ma vie", value: 3 }
            ]
          },
          {
            id: "perte_plaisir",
            label: "4. Perte de plaisir",
            options: [
              { label: "J'éprouve toujours autant de plaisir qu'avant aux choses qui me plaisent", value: 0 },
              { label: "Je n'éprouve pas autant de plaisir aux choses qu'avant", value: 1 },
              { label: "J'éprouve très peu de plaisir aux choses qui me plaisaient habituellement", value: 2 },
              { label: "Je n'éprouve aucun plaisir aux choses qui me plaisaient habituellement", value: 3 }
            ]
          },
          {
            id: "sentiments_culpabilite",
            label: "5. Sentiments de culpabilité",
            options: [
              { label: "Je ne me sens pas particulièrement coupable", value: 0 },
              { label: "Je me sens coupable pour bien des choses que j'ai faites ou aurais dû faire", value: 1 },
              { label: "Je me sens coupable la plupart du temps", value: 2 },
              { label: "Je me sens tout le temps coupable", value: 3 }
            ]
          },
          {
            id: "sentiment_punition",
            label: "6. Sentiment d'être puni(e)",
            options: [
              { label: "Je n'ai pas le sentiment d'être puni(e)", value: 0 },
              { label: "Je sens que je pourrais être puni(e)", value: 1 },
              { label: "Je m'attends à être puni(e)", value: 2 },
              { label: "J'ai le sentiment d'être puni(e)", value: 3 }
            ]
          },
          {
            id: "sentiments_negatifs",
            label: "7. Sentiments négatifs envers soi-même",
            options: [
              { label: "Mes sentiments envers moi-même n'ont pas changé", value: 0 },
              { label: "J'ai perdu confiance en moi", value: 1 },
              { label: "Je suis déçu(e) par moi-même", value: 2 },
              { label: "Je ne m'aime pas du tout", value: 3 }
            ]
          },
          {
            id: "attitude_critique",
            label: "8. Attitude critique envers soi",
            options: [
              { label: "Je ne me blâme pas ou ne me critique pas plus que d'habitude", value: 0 },
              { label: "Je suis plus critique envers moi-même que je ne l'étais", value: 1 },
              { label: "Je me reproche tous mes défauts", value: 2 },
              { label: "Je me reproche tous les malheurs qui arrivent", value: 3 }
            ]
          },
          {
            id: "pensees_suicidaires",
            label: "9. Pensées ou désirs de suicide",
            options: [
              { label: "Je ne pense pas du tout à me suicider", value: 0 },
              { label: "Il m'arrive de penser à me suicider, mais je ne le ferais pas", value: 1 },
              { label: "J'aimerais me suicider", value: 2 },
              { label: "Je me suiciderais si l'occasion se présentait", value: 3 }
            ]
          },
          {
            id: "pleurs",
            label: "10. Pleurs",
            options: [
              { label: "Je ne pleure pas plus qu'avant", value: 0 },
              { label: "Je pleure plus qu'avant", value: 1 },
              { label: "Je pleure pour la moindre petite chose", value: 2 },
              { label: "Je voudrais pleurer mais je n'en suis pas capable", value: 3 }
            ]
          },
          {
            id: "agitation",
            label: "11. Agitation",
            options: [
              { label: "Je ne suis pas plus agité(e) ou plus tendu(e) que d'habitude", value: 0 },
              { label: "Je me sens plus agité(e) ou plus tendu(e) que d'habitude", value: 1 },
              { label: "Je suis si agité(e) ou tendu(e) que j'ai du mal à rester tranquille", value: 2 },
              { label: "Je suis si agité(e) ou tendu(e) que je dois continuellement bouger ou faire quelque chose", value: 3 }
            ]
          },
          {
            id: "perte_interet",
            label: "12. Perte d'intérêt",
            options: [
              { label: "Je n'ai pas perdu d'intérêt pour les gens ou pour les activités", value: 0 },
              { label: "Je m'intéresse moins qu'avant aux gens et aux choses", value: 1 },
              { label: "Je ne m'intéresse presque plus aux gens et aux choses", value: 2 },
              { label: "J'ai du mal à m'intéresser à quoi que ce soit", value: 3 }
            ]
          },
          {
            id: "indecision",
            label: "13. Indécision",
            options: [
              { label: "Je prends des décisions toujours aussi bien qu'avant", value: 0 },
              { label: "Il m'est plus difficile que d'habitude de prendre des décisions", value: 1 },
              { label: "J'ai beaucoup plus de mal qu'avant à prendre des décisions", value: 2 },
              { label: "J'ai du mal à prendre n'importe quelle décision", value: 3 }
            ]
          },
          {
            id: "devalorisation",
            label: "14. Dévalorisation",
            options: [
              { label: "Je pense être quelqu'un de valable", value: 0 },
              { label: "Je ne crois pas avoir autant de valeur ni être aussi utile qu'avant", value: 1 },
              { label: "Je me sens moins valable que les autres", value: 2 },
              { label: "Je sens que je ne vaux absolument rien", value: 3 }
            ]
          },
          {
            id: "perte_energie",
            label: "15. Perte d'énergie",
            options: [
              { label: "J'ai toujours autant d'énergie qu'avant", value: 0 },
              { label: "J'ai moins d'énergie qu'avant", value: 1 },
              { label: "Je n'ai pas assez d'énergie pour pouvoir faire grand-chose", value: 2 },
              { label: "J'ai trop peu d'énergie pour faire quoi que ce soit", value: 3 }
            ]
          },
          {
            id: "modification_sommeil",
            label: "16. Modification dans les habitudes de sommeil",
            options: [
              { label: "Mes habitudes de sommeil n'ont pas changé", value: 0 },
              { label: "Je dors un peu plus/moins que d'habitude", value: 1 },
              { label: "Je dors beaucoup plus/moins que d'habitude", value: 2 },
              { label: "Je dors presque toute la journée / Je me réveille 1-2h trop tôt et ne peux me rendormir", value: 3 }
            ]
          },
          {
            id: "irritabilite",
            label: "17. Irritabilité",
            options: [
              { label: "Je ne suis pas plus irritable que d'habitude", value: 0 },
              { label: "Je suis plus irritable que d'habitude", value: 1 },
              { label: "Je suis beaucoup plus irritable que d'habitude", value: 2 },
              { label: "Je suis constamment irritable", value: 3 }
            ]
          },
          {
            id: "modification_appetit",
            label: "18. Modification de l'appétit",
            options: [
              { label: "Mon appétit n'a pas changé", value: 0 },
              { label: "J'ai un peu moins/plus d'appétit que d'habitude", value: 1 },
              { label: "J'ai beaucoup moins/plus d'appétit que d'habitude", value: 2 },
              { label: "Je n'ai pas d'appétit du tout / J'ai constamment envie de manger", value: 3 }
            ]
          },
          {
            id: "difficulte_concentration",
            label: "19. Difficulté à se concentrer",
            options: [
              { label: "Je parviens à me concentrer toujours aussi bien qu'avant", value: 0 },
              { label: "Je ne parviens pas à me concentrer aussi bien que d'habitude", value: 1 },
              { label: "J'ai du mal à me concentrer longtemps sur quoi que ce soit", value: 2 },
              { label: "Je me trouve incapable de me concentrer sur quoi que ce soit", value: 3 }
            ]
          },
          {
            id: "fatigue",
            label: "20. Fatigue",
            options: [
              { label: "Je ne suis pas plus fatigué(e) que d'habitude", value: 0 },
              { label: "Je me fatigue plus facilement que d'habitude", value: 1 },
              { label: "Je suis trop fatigué(e) pour faire un grand nombre de choses que je faisais avant", value: 2 },
              { label: "Je suis trop fatigué(e) pour faire la plupart des choses que je faisais avant", value: 3 }
            ]
          },
          {
            id: "perte_interet_sexuel",
            label: "21. Perte d'intérêt pour le sexe",
            options: [
              { label: "Je n'ai pas noté de changement récent dans mon intérêt pour le sexe", value: 0 },
              { label: "Le sexe m'intéresse moins qu'avant", value: 1 },
              { label: "Le sexe m'intéresse beaucoup moins maintenant", value: 2 },
              { label: "J'ai perdu tout intérêt pour le sexe", value: 3 }
            ]
          }
        ],
        interpretation: (score) => {
          if (score <= 13) return { level: "minimal", text: `Dépression minimale (${score}/63)`, color: "text-green-400", action: "Pas de dépression significative" };
          if (score <= 19) return { level: "leger", text: `Dépression légère (${score}/63)`, color: "text-yellow-400", action: "Surveillance, hygiène de vie, psychoéducation, revoir dans 2-4 semaines" };
          if (score <= 28) return { level: "modere", text: `Dépression modérée (${score}/63)`, color: "text-orange-400", action: "Psychothérapie recommandée, envisager traitement antidépresseur" };
          return { level: "severe", text: `Dépression sévère (${score}/63)`, color: "text-red-400", action: "Traitement antidépresseur + psychothérapie, évaluer risque suicidaire, avis psychiatrique" };
        }
      },
      {
        id: "mini",
        name: "MINI",
        fullName: "Mini International Neuropsychiatric Interview",
        description: "Entretien diagnostique structuré - Dépistage des principaux troubles psychiatriques",
        source: "Sheehan DV et al. 1998 - Version française validée",
        cotation: { code: "ALQP003", libelle: "Entretien psychiatrique structuré", tarif: 69.12 },
        miniModules: [
          {
            id: "A_edm",
            name: "Module A - Épisode Dépressif Majeur",
            description: "Critères DSM-5 de l'EDM actuel et passé",
            questions: [
              { id: "A1", text: "Au cours des 2 dernières semaines, vous êtes-vous senti(e) particulièrement triste, cafardeux(se), déprimé(e), la plupart du temps au cours de la journée, et ce, presque tous les jours ?", required: true },
              { id: "A2", text: "Au cours des 2 dernières semaines, aviez-vous presque tout le temps le sentiment de n'avoir plus goût à rien, d'avoir perdu l'intérêt ou le plaisir pour les choses qui vous plaisent habituellement ?", required: true },
              { id: "A3a", text: "Votre appétit a-t-il notablement changé, ou avez-vous pris ou perdu du poids sans en avoir l'intention ?", required: false },
              { id: "A3b", text: "Aviez-vous des problèmes de sommeil presque toutes les nuits (difficulté à s'endormir, réveils nocturnes ou précoces, ou dormir trop) ?", required: false },
              { id: "A3c", text: "Parliez-vous ou vous déplaciez-vous plus lentement que d'habitude, ou au contraire vous sentiez-vous agité(e) et aviez-vous du mal à rester en place ?", required: false },
              { id: "A3d", text: "Vous sentiez-vous presque tout le temps fatigué(e), sans énergie ?", required: false },
              { id: "A3e", text: "Vous sentiez-vous sans valeur ou coupable ?", required: false },
              { id: "A3f", text: "Aviez-vous du mal à vous concentrer ou à prendre des décisions ?", required: false },
              { id: "A3g", text: "Avez-vous eu à plusieurs reprises des idées noires comme penser qu'il vaudrait mieux que vous soyez mort(e), ou avez-vous pensé à vous faire du mal ?", required: false }
            ],
            scoring: "Si OUI à A1 ou A2 + ≥5 symptômes (A1-A3g) = EDM probable",
            interpretation: (answers) => {
              const a1 = answers.A1;
              const a2 = answers.A2;
              const sympCount = ['A1', 'A2', 'A3a', 'A3b', 'A3c', 'A3d', 'A3e', 'A3f', 'A3g'].filter(k => answers[k]).length;
              if ((a1 || a2) && sympCount >= 5) return { positive: true, text: "EDM probable", color: "text-red-400" };
              if (a1 || a2) return { positive: false, text: "Symptômes dépressifs mais critères EDM non remplis", color: "text-yellow-400" };
              return { positive: false, text: "Pas d'EDM", color: "text-green-400" };
            }
          },
          {
            id: "B_dysthymie",
            name: "Module B - Dysthymie",
            description: "Trouble dépressif persistant (≥2 ans)",
            questions: [
              { id: "B1", text: "Au cours des 2 dernières années, vous êtes-vous senti(e) triste, cafardeux(se) ou déprimé(e), la plupart du temps ?", required: true },
              { id: "B2", text: "Durant cette période, vous est-il arrivé de vous sentir bien pendant plus de 2 mois ?", required: true, inverted: true },
              { id: "B3a", text: "Votre appétit a-t-il changé de façon importante ?", required: false },
              { id: "B3b", text: "Avez-vous des problèmes de sommeil ou dormez-vous trop ?", required: false },
              { id: "B3c", text: "Vous sentez-vous fatigué(e) ou manquez-vous d'énergie ?", required: false },
              { id: "B3d", text: "Avez-vous peu confiance en vous-même ?", required: false },
              { id: "B3e", text: "Avez-vous du mal à vous concentrer ou à prendre des décisions ?", required: false },
              { id: "B3f", text: "Avez-vous le sentiment que les choses ne s'arrangeront jamais (sentiment de désespoir) ?", required: false }
            ],
            scoring: "Si OUI à B1 + NON à B2 + ≥2 symptômes B3 = Dysthymie probable",
            interpretation: (answers) => {
              const b1 = answers.B1;
              const b2 = answers.B2;
              const sympCount = ['B3a', 'B3b', 'B3c', 'B3d', 'B3e', 'B3f'].filter(k => answers[k]).length;
              if (b1 && !b2 && sympCount >= 2) return { positive: true, text: "Dysthymie probable", color: "text-orange-400" };
              return { positive: false, text: "Pas de dysthymie", color: "text-green-400" };
            }
          },
          {
            id: "C_suicidalite",
            name: "Module C - Risque Suicidaire",
            description: "Évaluation du risque suicidaire actuel",
            questions: [
              { id: "C1", text: "Au cours du mois écoulé, avez-vous pensé qu'il vaudrait mieux que vous soyez mort(e) ou avez-vous souhaité être mort(e) ?", points: 1 },
              { id: "C2", text: "Au cours du mois écoulé, avez-vous voulu vous faire du mal ?", points: 2 },
              { id: "C3", text: "Au cours du mois écoulé, avez-vous pensé à vous suicider ?", points: 6 },
              { id: "C4", text: "Au cours du mois écoulé, avez-vous établi un plan précis pour vous suicider ?", points: 10 },
              { id: "C5", text: "Au cours du mois écoulé, avez-vous fait une tentative de suicide ?", points: 10 },
              { id: "C6", text: "Avez-vous déjà fait une tentative de suicide au cours de votre vie ?", points: 4 }
            ],
            scoring: "Score 1-5: Risque léger | 6-9: Risque modéré | ≥10: Risque élevé",
            interpretation: (answers) => {
              let score = 0;
              if (answers.C1) score += 1;
              if (answers.C2) score += 2;
              if (answers.C3) score += 6;
              if (answers.C4) score += 10;
              if (answers.C5) score += 10;
              if (answers.C6) score += 4;
              if (score === 0) return { positive: false, text: "Pas de risque suicidaire identifié", color: "text-green-400", score: 0 };
              if (score <= 5) return { positive: true, text: `Risque suicidaire LÉGER (${score})`, color: "text-yellow-400", score };
              if (score <= 9) return { positive: true, text: `Risque suicidaire MODÉRÉ (${score})`, color: "text-orange-400", score };
              return { positive: true, text: `Risque suicidaire ÉLEVÉ (${score}) - URGENCE`, color: "text-red-600", score };
            }
          },
          {
            id: "D_manie",
            name: "Module D - Épisode Maniaque/Hypomaniaque",
            description: "Critères du trouble bipolaire",
            questions: [
              { id: "D1a", text: "Avez-vous déjà eu une période où vous vous sentiez tellement « en forme », « remonté(e) » ou plein(e) d'énergie que cela vous a posé des problèmes ?", required: true },
              { id: "D1b", text: "Vous êtes-vous déjà senti(e) tellement irritable que vous vous êtes mis(e) à crier, à vous battre ou avez insulté les gens ?", required: true },
              { id: "D2a", text: "Durant cette période, aviez-vous le sentiment que vous pouviez faire des choses que les autres ne pouvaient pas faire, ou que vous étiez quelqu'un de particulièrement important ?", required: false },
              { id: "D2b", text: "Aviez-vous moins besoin de sommeil qu'habituellement (vous sentiez-vous reposé(e) après quelques heures de sommeil seulement) ?", required: false },
              { id: "D2c", text: "Parliez-vous beaucoup plus ou plus vite qu'habituellement ?", required: false },
              { id: "D2d", text: "Vos pensées défilaient-elles si vite dans votre tête que vous ne pouviez pas bien les suivre ?", required: false },
              { id: "D2e", text: "Étiez-vous si facilement distrait(e) par ce qui se passait autour de vous que vous aviez du mal à vous concentrer ?", required: false },
              { id: "D2f", text: "Étiez-vous tellement actif(ve), agité(e), que les autres s'inquiétaient pour vous ?", required: false },
              { id: "D2g", text: "Aviez-vous tellement envie de faire des choses agréables que vous avez fait des choses excessives ou risquées ?", required: false }
            ],
            scoring: "Si OUI à D1a ou D1b + ≥3 symptômes D2 = Épisode maniaque/hypomaniaque probable",
            interpretation: (answers) => {
              const d1 = answers.D1a || answers.D1b;
              const sympCount = ['D2a', 'D2b', 'D2c', 'D2d', 'D2e', 'D2f', 'D2g'].filter(k => answers[k]).length;
              if (d1 && sympCount >= 3) return { positive: true, text: "Épisode maniaque/hypomaniaque probable - Évoquer trouble bipolaire", color: "text-red-400" };
              return { positive: false, text: "Pas d'épisode maniaque/hypomaniaque", color: "text-green-400" };
            }
          },
          {
            id: "E_panique",
            name: "Module E - Trouble Panique",
            description: "Attaques de panique récurrentes",
            questions: [
              { id: "E1", text: "Avez-vous déjà eu, à plusieurs reprises, des crises ou des attaques durant lesquelles vous vous êtes senti(e) soudainement très anxieux(se), très mal à l'aise ou effrayé(e), même dans des situations où la plupart des gens ne le seraient pas ?", required: true },
              { id: "E2", text: "Ces crises atteignaient-elles leur maximum d'intensité en moins de 10 minutes ?", required: true },
              { id: "E3a", text: "Durant ces attaques, aviez-vous des palpitations ou votre cœur battait-il très fort ?", required: false },
              { id: "E3b", text: "Aviez-vous des sueurs ou des mains moites ?", required: false },
              { id: "E3c", text: "Aviez-vous des tremblements ?", required: false },
              { id: "E3d", text: "Aviez-vous du mal à respirer ou étiez-vous essoufflé(e) ?", required: false },
              { id: "E3e", text: "Aviez-vous une sensation d'étranglement ou une boule dans la gorge ?", required: false },
              { id: "E3f", text: "Aviez-vous des douleurs ou une gêne dans la poitrine ?", required: false },
              { id: "E3g", text: "Aviez-vous des nausées, une gêne dans l'estomac ou une diarrhée soudaine ?", required: false },
              { id: "E3h", text: "Vous sentiez-vous étourdi(e), pris(e) de vertiges ou sur le point de vous évanouir ?", required: false },
              { id: "E3i", text: "Aviez-vous l'impression que les choses autour de vous étaient irréelles ou vous sentiez-vous comme détaché(e) de vous-même ?", required: false },
              { id: "E3j", text: "Aviez-vous peur de perdre le contrôle ou de devenir fou/folle ?", required: false },
              { id: "E3k", text: "Aviez-vous peur de mourir ?", required: false },
              { id: "E3l", text: "Aviez-vous des engourdissements ou des picotements ?", required: false },
              { id: "E3m", text: "Aviez-vous des bouffées de chaleur ou des frissons ?", required: false }
            ],
            scoring: "Si OUI à E1 + E2 + ≥4 symptômes E3 = Trouble panique probable",
            interpretation: (answers) => {
              const e1 = answers.E1;
              const e2 = answers.E2;
              const sympCount = ['E3a', 'E3b', 'E3c', 'E3d', 'E3e', 'E3f', 'E3g', 'E3h', 'E3i', 'E3j', 'E3k', 'E3l', 'E3m'].filter(k => answers[k]).length;
              if (e1 && e2 && sympCount >= 4) return { positive: true, text: "Trouble panique probable", color: "text-orange-400" };
              if (e1) return { positive: false, text: "Attaques de panique mais critères du trouble non remplis", color: "text-yellow-400" };
              return { positive: false, text: "Pas de trouble panique", color: "text-green-400" };
            }
          },
          {
            id: "F_agoraphobie",
            name: "Module F - Agoraphobie",
            description: "Peur des espaces/situations",
            questions: [
              { id: "F1", text: "Êtes-vous anxieux(se) ou avez-vous peur de vous trouver dans des endroits ou des situations d'où il serait difficile de s'échapper ou dans lesquels vous ne pourriez pas avoir d'aide en cas de malaise ?", required: true },
              { id: "F2a", text: "Avez-vous peur d'utiliser les transports en commun ?", required: false },
              { id: "F2b", text: "Avez-vous peur de vous retrouver dans des espaces ouverts ?", required: false },
              { id: "F2c", text: "Avez-vous peur de vous retrouver dans des espaces clos ?", required: false },
              { id: "F2d", text: "Avez-vous peur de faire la queue ou d'être dans une foule ?", required: false },
              { id: "F2e", text: "Avez-vous peur de vous retrouver seul(e) hors de chez vous ?", required: false }
            ],
            scoring: "Si OUI à F1 + ≥2 situations F2 = Agoraphobie probable",
            interpretation: (answers) => {
              const f1 = answers.F1;
              const situCount = ['F2a', 'F2b', 'F2c', 'F2d', 'F2e'].filter(k => answers[k]).length;
              if (f1 && situCount >= 2) return { positive: true, text: "Agoraphobie probable", color: "text-orange-400" };
              return { positive: false, text: "Pas d'agoraphobie", color: "text-green-400" };
            }
          },
          {
            id: "G_phobie_sociale",
            name: "Module G - Phobie Sociale",
            description: "Anxiété sociale",
            questions: [
              { id: "G1", text: "Au cours du mois écoulé, avez-vous eu peur ou vous êtes-vous senti(e) gêné(e) ou mal à l'aise lorsque vous étiez sous le regard des autres ou que vous étiez le centre de l'attention ?", required: true },
              { id: "G2", text: "Cette peur vous a-t-elle semblé excessive ou déraisonnable ?", required: true },
              { id: "G3", text: "Avez-vous évité certaines situations à cause de cette peur ?", required: true },
              { id: "G4", text: "Cette peur entraîne-t-elle chez vous une souffrance importante ou vous gêne-t-elle dans votre travail, vos activités ou vos relations avec les autres ?", required: true }
            ],
            scoring: "Si OUI à G1 + G2 + G3 + G4 = Phobie sociale probable",
            interpretation: (answers) => {
              if (answers.G1 && answers.G2 && answers.G3 && answers.G4) return { positive: true, text: "Phobie sociale probable", color: "text-orange-400" };
              if (answers.G1) return { positive: false, text: "Anxiété sociale présente mais critères non remplis", color: "text-yellow-400" };
              return { positive: false, text: "Pas de phobie sociale", color: "text-green-400" };
            }
          },
          {
            id: "H_toc",
            name: "Module H - Trouble Obsessionnel Compulsif",
            description: "Obsessions et compulsions",
            questions: [
              { id: "H1", text: "Au cours du mois écoulé, avez-vous été envahi(e) par des pensées, des impulsions ou des images désagréables ou angoissantes, qui revenaient sans arrêt à votre esprit alors que vous ne le souhaitiez pas ?", required: true },
              { id: "H2", text: "Au cours du mois écoulé, avez-vous fait certaines choses encore et encore sans pouvoir vous en empêcher (vérifier, laver, compter, ranger, etc.) ?", required: true },
              { id: "H3", text: "Ces pensées ou comportements ont-ils occupé plus d'une heure par jour ?", required: true },
              { id: "H4", text: "Ces pensées ou comportements vous ont-ils fait souffrir ou ont-ils gêné votre vie quotidienne ?", required: true }
            ],
            scoring: "Si OUI à H1 ou H2 + H3 + H4 = TOC probable",
            interpretation: (answers) => {
              const h12 = answers.H1 || answers.H2;
              if (h12 && answers.H3 && answers.H4) return { positive: true, text: "TOC probable", color: "text-orange-400" };
              if (h12) return { positive: false, text: "Symptômes obsessionnels-compulsifs mais critères non remplis", color: "text-yellow-400" };
              return { positive: false, text: "Pas de TOC", color: "text-green-400" };
            }
          },
          {
            id: "I_espt",
            name: "Module I - État de Stress Post-Traumatique",
            description: "ESPT suite à un événement traumatique",
            questions: [
              { id: "I1", text: "Avez-vous déjà vécu, été témoin ou été confronté(e) à un événement extrêmement traumatique où des personnes ont pu mourir ou être gravement blessées, ou être menacées de mort ou de graves blessures ?", required: true },
              { id: "I2", text: "Au cours du mois écoulé, avez-vous souvent repensé à cet événement de façon pénible ou en avez-vous rêvé ou fait des cauchemars ?", required: true },
              { id: "I3", text: "Avez-vous essayé de ne plus y penser ou avez-vous évité tout ce qui pouvait vous le rappeler ?", required: true },
              { id: "I4", text: "Avez-vous été constamment sur vos gardes, vigilant(e) ou sursautiez-vous facilement ?", required: true },
              { id: "I5", text: "Ces problèmes ont-ils commencé ou se sont-ils aggravés après l'événement traumatique ?", required: true }
            ],
            scoring: "Si OUI à I1 + I2 + I3 + I4 + I5 = ESPT probable",
            interpretation: (answers) => {
              if (answers.I1 && answers.I2 && answers.I3 && answers.I4 && answers.I5) return { positive: true, text: "ESPT probable", color: "text-red-400" };
              if (answers.I1 && answers.I2) return { positive: false, text: "Symptômes post-traumatiques mais critères ESPT non remplis", color: "text-yellow-400" };
              return { positive: false, text: "Pas d'ESPT", color: "text-green-400" };
            }
          },
          {
            id: "J_tag",
            name: "Module J - Trouble Anxieux Généralisé",
            description: "Anxiété chronique excessive",
            questions: [
              { id: "J1", text: "Au cours des 6 derniers mois, vous êtes-vous senti(e) excessivement préoccupé(e), inquiet(e), anxieux(se) pour des problèmes de la vie de tous les jours ?", required: true },
              { id: "J2", text: "Avez-vous eu ces préoccupations presque tous les jours ?", required: true },
              { id: "J3", text: "Avez-vous du mal à contrôler ces préoccupations ou vous empêchent-elles de vous concentrer ?", required: true },
              { id: "J4a", text: "Vous sentez-vous agité(e), tendu(e), les nerfs à fleur de peau ?", required: false },
              { id: "J4b", text: "Avez-vous les muscles tendus ?", required: false },
              { id: "J4c", text: "Vous sentez-vous fatigué(e), faible, épuisé(e) facilement ?", required: false },
              { id: "J4d", text: "Avez-vous du mal à vous concentrer ou des trous de mémoire ?", required: false },
              { id: "J4e", text: "Êtes-vous irritable ?", required: false },
              { id: "J4f", text: "Avez-vous des problèmes de sommeil ?", required: false }
            ],
            scoring: "Si OUI à J1 + J2 + J3 + ≥3 symptômes J4 = TAG probable",
            interpretation: (answers) => {
              const j123 = answers.J1 && answers.J2 && answers.J3;
              const sympCount = ['J4a', 'J4b', 'J4c', 'J4d', 'J4e', 'J4f'].filter(k => answers[k]).length;
              if (j123 && sympCount >= 3) return { positive: true, text: "TAG probable", color: "text-orange-400" };
              if (answers.J1) return { positive: false, text: "Anxiété présente mais critères TAG non remplis", color: "text-yellow-400" };
              return { positive: false, text: "Pas de TAG", color: "text-green-400" };
            }
          }
        ],
        interpretation: (moduleResults) => {
          // Synthèse globale basée sur les résultats des modules
          const positiveModules = Object.entries(moduleResults).filter(([k, v]) => v.positive);
          if (positiveModules.length === 0) {
            return { level: "normal", text: "Aucun trouble psychiatrique majeur détecté", color: "text-green-400", action: "Pas de trouble identifié par le MINI. Surveillance clinique si symptômes persistants." };
          }
          const hasSuicideRisk = moduleResults.C_suicidalite?.positive;
          const hasEDM = moduleResults.A_edm?.positive;
          const hasBipolar = moduleResults.D_manie?.positive;
          
          if (hasSuicideRisk && moduleResults.C_suicidalite.score >= 10) {
            return { level: "urgent", text: `URGENCE - Risque suicidaire élevé + ${positiveModules.length} trouble(s)`, color: "text-red-600", action: "URGENCE PSYCHIATRIQUE - Évaluation immédiate du risque suicidaire, hospitalisation à discuter" };
          }
          if (hasEDM && hasBipolar) {
            return { level: "severe", text: "EDM + Épisode maniaque - Trouble bipolaire probable", color: "text-red-400", action: "Avis psychiatrique recommandé pour confirmer trouble bipolaire et initier thymorégulateur" };
          }
          if (positiveModules.length >= 3) {
            return { level: "severe", text: `${positiveModules.length} troubles identifiés - Comorbidités multiples`, color: "text-red-400", action: "Avis psychiatrique recommandé en raison de la comorbidité" };
          }
          return { level: "modere", text: `${positiveModules.length} trouble(s) identifié(s)`, color: "text-orange-400", action: "Prise en charge adaptée selon le(s) trouble(s) identifié(s). Envisager avis spécialisé." };
        }
      },
      {
        id: "gad7",
        name: "GAD-7",
        fullName: "Generalized Anxiety Disorder-7",
        description: "Dépistage du trouble anxieux généralisé",
        cotation: { code: "ALQP003", libelle: "Évaluation psychiatrique", tarif: 69.12 },
        questions: [
          "Nervosité, anxiété ou tension",
          "Incapacité à contrôler ses inquiétudes",
          "Inquiétude excessive pour différentes choses",
          "Difficulté à se détendre",
          "Agitation, impossibilité de tenir en place",
          "Facilement contrarié(e) ou irritable",
          "Peur que quelque chose de terrible arrive"
        ],
        options: [
          { label: "Jamais", value: 0 },
          { label: "Plusieurs jours", value: 1 },
          { label: "Plus de la moitié du temps", value: 2 },
          { label: "Presque tous les jours", value: 3 }
        ],
        interpretation: (score) => {
          if (score <= 4) return { level: "minimal", text: `Anxiété minimale (${score})`, color: "text-green-400", action: "Pas d'intervention" };
          if (score <= 9) return { level: "leger", text: `Anxiété légère (${score})`, color: "text-yellow-400", action: "Relaxation, surveillance" };
          if (score <= 14) return { level: "modere", text: `Anxiété modérée (${score})`, color: "text-orange-400", action: "TCC ± traitement médicamenteux" };
          return { level: "severe", text: `Anxiété sévère (${score})`, color: "text-red-400", action: "Traitement actif recommandé, avis spécialisé" };
        }
      },
      {
        id: "had",
        name: "HAD",
        fullName: "Hospital Anxiety and Depression Scale",
        description: "Anxiété et dépression (14 items, 2 sous-scores)",
        cotation: { code: "ALQP003", libelle: "Évaluation psychiatrique", tarif: 69.12 },
        subscales: ["anxiete", "depression"],
        questions: [
          { text: "Je me sens tendu(e) ou énervé(e)", subscale: "anxiete", options: [
            { label: "Jamais", value: 0 }, { label: "De temps en temps", value: 1 }, { label: "Souvent", value: 2 }, { label: "La plupart du temps", value: 3 }
          ]},
          { text: "Je prends plaisir aux mêmes choses qu'autrefois", subscale: "depression", options: [
            { label: "Oui, tout autant", value: 0 }, { label: "Pas autant", value: 1 }, { label: "Un peu seulement", value: 2 }, { label: "Presque plus", value: 3 }
          ]},
          { text: "J'ai une sensation de peur, quelque chose d'horrible va m'arriver", subscale: "anxiete", options: [
            { label: "Pas du tout", value: 0 }, { label: "Un peu", value: 1 }, { label: "Oui, pas trop grave", value: 2 }, { label: "Oui, très nettement", value: 3 }
          ]},
          { text: "Je ris et vois le bon côté des choses", subscale: "depression", options: [
            { label: "Autant que par le passé", value: 0 }, { label: "Plus autant", value: 1 }, { label: "Vraiment moins", value: 2 }, { label: "Plus du tout", value: 3 }
          ]},
          { text: "Je me fais du souci", subscale: "anxiete", options: [
            { label: "Très occasionnellement", value: 0 }, { label: "Occasionnellement", value: 1 }, { label: "Assez souvent", value: 2 }, { label: "Très souvent", value: 3 }
          ]},
          { text: "Je suis de bonne humeur", subscale: "depression", options: [
            { label: "La plupart du temps", value: 0 }, { label: "Assez souvent", value: 1 }, { label: "Rarement", value: 2 }, { label: "Jamais", value: 3 }
          ]},
          { text: "Je peux rester tranquillement assis(e)", subscale: "anxiete", options: [
            { label: "Oui, quoi qu'il arrive", value: 0 }, { label: "Oui, en général", value: 1 }, { label: "Rarement", value: 2 }, { label: "Jamais", value: 3 }
          ]},
          { text: "J'ai l'impression de fonctionner au ralenti", subscale: "depression", options: [
            { label: "Jamais", value: 0 }, { label: "Parfois", value: 1 }, { label: "Très souvent", value: 2 }, { label: "Presque toujours", value: 3 }
          ]},
          { text: "Sensations de peur, estomac noué", subscale: "anxiete", options: [
            { label: "Jamais", value: 0 }, { label: "Parfois", value: 1 }, { label: "Assez souvent", value: 2 }, { label: "Très souvent", value: 3 }
          ]},
          { text: "Je ne m'intéresse plus à mon apparence", subscale: "depression", options: [
            { label: "Autant d'attention", value: 0 }, { label: "Pas autant", value: 1 }, { label: "Plus beaucoup", value: 2 }, { label: "Plus du tout", value: 3 }
          ]},
          { text: "J'ai la bougeotte, n'arrive pas à tenir en place", subscale: "anxiete", options: [
            { label: "Pas du tout", value: 0 }, { label: "Pas tellement", value: 1 }, { label: "Un peu", value: 2 }, { label: "Oui, c'est le cas", value: 3 }
          ]},
          { text: "Je me réjouis à l'idée de faire certaines choses", subscale: "depression", options: [
            { label: "Autant qu'avant", value: 0 }, { label: "Un peu moins", value: 1 }, { label: "Bien moins", value: 2 }, { label: "Presque jamais", value: 3 }
          ]},
          { text: "J'éprouve des sensations soudaines de panique", subscale: "anxiete", options: [
            { label: "Jamais", value: 0 }, { label: "Pas très souvent", value: 1 }, { label: "Assez souvent", value: 2 }, { label: "Vraiment très souvent", value: 3 }
          ]},
          { text: "Je peux prendre plaisir à un livre ou une émission", subscale: "depression", options: [
            { label: "Souvent", value: 0 }, { label: "Parfois", value: 1 }, { label: "Rarement", value: 2 }, { label: "Très rarement", value: 3 }
          ]}
        ],
        interpretation: (scores) => {
          const interpretSub = (score, type) => {
            if (score <= 7) return { level: "normal", text: `${type}: Normal (${score})`, color: "text-green-400" };
            if (score <= 10) return { level: "douteux", text: `${type}: Douteux (${score})`, color: "text-yellow-400" };
            return { level: "certain", text: `${type}: Avéré (${score})`, color: "text-red-400" };
          };
          return {
            anxiete: interpretSub(scores.anxiete, "Anxiété"),
            depression: interpretSub(scores.depression, "Dépression"),
            action: scores.anxiete > 10 || scores.depression > 10 ? "Prise en charge spécialisée" : "Surveillance"
          };
        }
      },
      {
        id: "cage",
        name: "CAGE",
        fullName: "Cut-down, Annoyed, Guilty, Eye-opener",
        description: "Dépistage rapide des problèmes d'alcool (4 questions)",
        source: "Ewing JA, 1984 - Validé en français (DETA)",
        cotation: null,
        cageQuestions: [
          {
            id: "cut_down",
            label: "C - Cut-down (Réduire)",
            text: "Avez-vous déjà ressenti le besoin de DIMINUER votre consommation d'alcool ?",
            letter: "C"
          },
          {
            id: "annoyed",
            label: "A - Annoyed (Agacé)",
            text: "Votre entourage vous a-t-il déjà fait des REMARQUES au sujet de votre consommation ?",
            letter: "A"
          },
          {
            id: "guilty",
            label: "G - Guilty (Culpabilité)",
            text: "Avez-vous déjà eu l'impression que vous buviez TROP ?",
            letter: "G"
          },
          {
            id: "eye_opener",
            label: "E - Eye-opener (Réveil)",
            text: "Avez-vous déjà eu besoin d'ALCOOL dès le MATIN pour vous sentir en forme ?",
            letter: "E"
          }
        ],
        interpretation: (score) => {
          if (score === 0) return { level: "negatif", text: "CAGE négatif (0/4)", color: "text-green-400", action: "Pas de problème d'alcool détecté" };
          if (score === 1) return { level: "possible", text: `CAGE limite (${score}/4)`, color: "text-yellow-400", action: "Interrogatoire complémentaire recommandé (AUDIT)" };
          if (score === 2) return { level: "probable", text: `CAGE positif (${score}/4)`, color: "text-orange-400", action: "Mésusage probable, proposer AUDIT complet" };
          return { level: "certain", text: `CAGE très positif (${score}/4)`, color: "text-red-400", action: "Dépendance probable, orientation alcoologique" };
        }
      },
      {
        id: "audit_c",
        name: "AUDIT-C",
        fullName: "Alcohol Use Disorders Identification Test - Consumption",
        description: "Dépistage du mésusage d'alcool",
        cotation: null,
        questions: [
          { text: "À quelle fréquence consommez-vous de l'alcool?", options: [
            { label: "Jamais", value: 0 }, { label: "≤1 fois/mois", value: 1 }, { label: "2-4 fois/mois", value: 2 }, { label: "2-3 fois/semaine", value: 3 }, { label: "≥4 fois/semaine", value: 4 }
          ]},
          { text: "Combien de verres standard en une journée typique?", options: [
            { label: "1 ou 2", value: 0 }, { label: "3 ou 4", value: 1 }, { label: "5 ou 6", value: 2 }, { label: "7 à 9", value: 3 }, { label: "10 ou plus", value: 4 }
          ]},
          { text: "À quelle fréquence ≥6 verres en une occasion?", options: [
            { label: "Jamais", value: 0 }, { label: "<1 fois/mois", value: 1 }, { label: "1 fois/mois", value: 2 }, { label: "1 fois/semaine", value: 3 }, { label: "Quotidien ou presque", value: 4 }
          ]}
        ],
        interpretation: (score) => {
          // Seuils différents H/F
          if (score <= 3) return { level: "normal", text: `Consommation à faible risque (${score}/12)`, color: "text-green-400", action: "Renforcement positif" };
          if (score <= 7) return { level: "risque", text: `Consommation à risque (${score}/12)`, color: "text-yellow-400", action: "Intervention brève, conseil de réduction" };
          return { level: "nocif", text: `Usage nocif probable (${score}/12)`, color: "text-red-400", action: "AUDIT complet, évaluation alcoologique" };
        }
      },
      {
        id: "fagerstrom",
        name: "Fagerström",
        fullName: "Test de Fagerström simplifié",
        description: "Dépendance nicotinique",
        cotation: null,
        questions: [
          { text: "Combien de temps après le réveil fumez-vous votre première cigarette?", options: [
            { label: "≤5 minutes", value: 3 }, { label: "6-30 minutes", value: 2 }, { label: "31-60 minutes", value: 1 }, { label: ">60 minutes", value: 0 }
          ]},
          { text: "Combien de cigarettes fumez-vous par jour?", options: [
            { label: "≤10", value: 0 }, { label: "11-20", value: 1 }, { label: "21-30", value: 2 }, { label: ">30", value: 3 }
          ]}
        ],
        interpretation: (score) => {
          if (score <= 1) return { level: "faible", text: `Dépendance faible (${score}/6)`, color: "text-green-400", action: "Sevrage sans aide médicamenteuse possible" };
          if (score <= 3) return { level: "moyenne", text: `Dépendance moyenne (${score}/6)`, color: "text-yellow-400", action: "Substituts nicotiniques conseillés" };
          return { level: "forte", text: `Dépendance forte (${score}/6)`, color: "text-red-400", action: "Substituts nicotiniques fortement recommandés ± varénicline" };
        }
      }
    ]
  },

  // ============================================
  // 7. DOULEUR
  // ============================================
  douleur: {
    name: "Douleur",
    icon: "Activity",
    color: "from-pink-600 to-rose-600",
    scores: [
      {
        id: "eva",
        name: "EVA / EN",
        fullName: "Échelle Visuelle Analogique / Numérique",
        description: "Intensité de la douleur (0-10)",
        cotation: null,
        fields: [
          { id: "score", label: "Intensité (0 = pas de douleur, 10 = douleur maximale)", type: "range", min: 0, max: 10 }
        ],
        interpretation: (score) => {
          if (score <= 3) return { level: "legere", text: `Douleur légère (${score}/10)`, color: "text-green-400", action: "Palier 1 (paracétamol)" };
          if (score <= 5) return { level: "moderee", text: `Douleur modérée (${score}/10)`, color: "text-yellow-400", action: "Palier 1-2, AINS si indiqué" };
          if (score <= 7) return { level: "intense", text: `Douleur intense (${score}/10)`, color: "text-orange-400", action: "Palier 2-3, réévaluation rapide" };
          return { level: "tres_intense", text: `Douleur très intense (${score}/10)`, color: "text-red-400", action: "Palier 3, évaluation spécialisée" };
        }
      },
      {
        id: "dn4",
        name: "DN4",
        fullName: "Douleur Neuropathique en 4 Questions",
        description: "Dépistage de la douleur neuropathique",
        cotation: { code: "ALQP003", libelle: "Évaluation douleur", tarif: 69.12 },
        questions: [
          { text: "La douleur a-t-elle des caractéristiques de brûlure?", section: "caracteristiques" },
          { text: "La douleur a-t-elle des caractéristiques de froid douloureux?", section: "caracteristiques" },
          { text: "La douleur a-t-elle des caractéristiques de décharges électriques?", section: "caracteristiques" },
          { text: "La douleur est-elle associée à des fourmillements?", section: "symptomes" },
          { text: "La douleur est-elle associée à des picotements?", section: "symptomes" },
          { text: "La douleur est-elle associée à un engourdissement?", section: "symptomes" },
          { text: "La douleur est-elle associée à des démangeaisons?", section: "symptomes" },
          { text: "Hypoesthésie au tact dans le territoire douloureux?", section: "examen" },
          { text: "Hypoesthésie à la piqûre dans le territoire douloureux?", section: "examen" },
          { text: "Douleur provoquée ou augmentée par le frottement?", section: "examen" }
        ],
        interpretation: (score) => {
          if (score < 4) return { level: "peu_probable", text: `DN peu probable (${score}/10)`, color: "text-green-400", action: "Traitement antalgique classique" };
          return { level: "probable", text: `DN probable (${score}/10)`, color: "text-red-400", action: "Traitement spécifique: prégabaline, duloxétine, amitriptyline" };
        }
      },
      {
        id: "algoplus",
        name: "Algoplus",
        fullName: "Échelle comportementale de douleur aiguë",
        description: "Douleur aiguë chez le sujet âgé non communiquant",
        cotation: null,
        items: [
          { name: "Visage (froncement sourcils, grimaces)", options: [{ label: "Oui", value: 1 }, { label: "Non", value: 0 }] },
          { name: "Regard (inattentif, fixe, vide, pleurs)", options: [{ label: "Oui", value: 1 }, { label: "Non", value: 0 }] },
          { name: "Plaintes (gémissements, cris)", options: [{ label: "Oui", value: 1 }, { label: "Non", value: 0 }] },
          { name: "Corps (protection, attitude antalgique)", options: [{ label: "Oui", value: 1 }, { label: "Non", value: 0 }] },
          { name: "Comportement (agitation, agressivité)", options: [{ label: "Oui", value: 1 }, { label: "Non", value: 0 }] }
        ],
        interpretation: (score) => {
          if (score < 2) return { level: "absent", text: `Douleur absente ou légère (${score}/5)`, color: "text-green-400", action: "Surveillance" };
          return { level: "present", text: `Douleur probable (${score}/5)`, color: "text-red-400", action: "Traitement antalgique à instaurer" };
        }
      }
    ]
  },

  // ============================================
  // 8. ENDOCRINOLOGIE / MÉTABOLISME
  // ============================================
  endocrinologie: {
    name: "Endocrinologie / Métabolisme",
    icon: "FlaskConical",
    color: "from-teal-600 to-cyan-600",
    scores: [
      {
        id: "imc",
        name: "IMC",
        fullName: "Indice de Masse Corporelle",
        description: "Évaluation du statut pondéral",
        cotation: null,
        fields: [
          { id: "poids", label: "Poids (kg)", type: "number", min: 30, max: 300 },
          { id: "taille", label: "Taille (cm)", type: "number", min: 100, max: 250 }
        ],
        calculate: (values) => {
          const tailleM = values.taille / 100;
          return Math.round((values.poids / (tailleM * tailleM)) * 10) / 10;
        },
        interpretation: (imc) => {
          if (imc < 16.5) return { level: "denutrition_severe", text: `Dénutrition sévère (IMC ${imc})`, color: "text-red-600", action: "Renutrition urgente" };
          if (imc < 18.5) return { level: "maigreur", text: `Maigreur (IMC ${imc})`, color: "text-red-400", action: "Rechercher cause, enrichissement" };
          if (imc < 25) return { level: "normal", text: `Poids normal (IMC ${imc})`, color: "text-green-400", action: "Maintien hygiène de vie" };
          if (imc < 30) return { level: "surpoids", text: `Surpoids (IMC ${imc})`, color: "text-yellow-400", action: "Conseils diététiques, activité physique" };
          if (imc < 35) return { level: "obesite_1", text: `Obésité grade I (IMC ${imc})`, color: "text-orange-400", action: "PEC pluridisciplinaire" };
          if (imc < 40) return { level: "obesite_2", text: `Obésité grade II (IMC ${imc})`, color: "text-red-400", action: "PEC spécialisée, chirurgie à discuter" };
          return { level: "obesite_3", text: `Obésité grade III (IMC ${imc})`, color: "text-red-600", action: "Chirurgie bariatrique à considérer" };
        }
      },
      {
        id: "findrisc",
        name: "FINDRISC",
        fullName: "Finnish Diabetes Risk Score",
        description: "Risque de diabète de type 2 à 10 ans",
        cotation: null,
        fields: [
          { id: "age", label: "Âge", type: "select", options: [
            { value: 0, label: "< 45 ans" }, { value: 2, label: "45-54 ans" }, { value: 3, label: "55-64 ans" }, { value: 4, label: "> 64 ans" }
          ]},
          { id: "imc", label: "IMC", type: "select", options: [
            { value: 0, label: "< 25" }, { value: 1, label: "25-30" }, { value: 3, label: "> 30" }
          ]},
          { id: "tour_taille", label: "Tour de taille", type: "select", options: [
            { value: 0, label: "H < 94 cm / F < 80 cm" }, { value: 3, label: "H 94-102 cm / F 80-88 cm" }, { value: 4, label: "H > 102 cm / F > 88 cm" }
          ]},
          { id: "activite", label: "Activité physique ≥30min/jour", type: "select", options: [
            { value: 0, label: "Oui" }, { value: 2, label: "Non" }
          ]},
          { id: "legumes", label: "Légumes/fruits tous les jours", type: "select", options: [
            { value: 0, label: "Oui" }, { value: 1, label: "Non" }
          ]},
          { id: "hta", label: "Traitement antihypertenseur", type: "select", options: [
            { value: 0, label: "Non" }, { value: 2, label: "Oui" }
          ]},
          { id: "glycemie", label: "Antécédent d'hyperglycémie", type: "select", options: [
            { value: 0, label: "Non" }, { value: 5, label: "Oui" }
          ]},
          { id: "famille", label: "Diabète dans la famille", type: "select", options: [
            { value: 0, label: "Non" }, { value: 3, label: "2ème degré" }, { value: 5, label: "1er degré" }
          ]}
        ],
        interpretation: (score) => {
          if (score < 7) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "Risque < 1%. Hygiène de vie" };
          if (score < 12) return { level: "legerement_eleve", text: `Risque légèrement élevé (${score})`, color: "text-yellow-400", action: "Risque ~4%. Modifications mode de vie" };
          if (score < 15) return { level: "modere", text: `Risque modéré (${score})`, color: "text-orange-400", action: "Risque ~17%. Glycémie à jeun annuelle" };
          if (score < 21) return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Risque ~33%. Dépistage actif" };
          return { level: "tres_eleve", text: `Risque très élevé (${score})`, color: "text-red-600", action: "Risque ~50%. HGPO recommandée" };
        }
      },
      {
        id: "frax",
        name: "FRAX",
        fullName: "Fracture Risk Assessment Tool",
        description: "Risque fracturaire ostéoporotique à 10 ans",
        cotation: { code: "PAQK007", libelle: "Ostéodensitométrie", tarif: 39.96 },
        fields: [
          { id: "age", label: "Âge (ans)", type: "number", min: 40, max: 90 },
          { id: "sexe", label: "Sexe", type: "select", options: [{ value: "femme", label: "Femme" }, { value: "homme", label: "Homme" }] },
          { id: "poids", label: "Poids (kg)", type: "number", min: 30, max: 150 },
          { id: "taille", label: "Taille (cm)", type: "number", min: 130, max: 210 },
          { id: "fracture", label: "Fracture de fragilité antérieure", type: "boolean" },
          { id: "hanche_parent", label: "Fracture hanche chez un parent", type: "boolean" },
          { id: "tabac", label: "Tabagisme actuel", type: "boolean" },
          { id: "corticoides", label: "Corticothérapie prolongée", type: "boolean" },
          { id: "pr", label: "Polyarthrite rhumatoïde", type: "boolean" },
          { id: "osteoporose", label: "Ostéoporose secondaire", type: "boolean" },
          { id: "alcool", label: "Alcool ≥ 3 verres/jour", type: "boolean" },
          { id: "dmo", label: "T-score col fémoral (si connu)", type: "number", min: -5, max: 2, step: 0.1 }
        ],
        calculate: (values) => {
          // Calcul simplifié (le vrai FRAX utilise des tables complexes)
          let risk = values.sexe === "femme" ? 5 : 3;
          risk += (values.age - 50) * 0.3;
          if (values.fracture) risk *= 2;
          if (values.hanche_parent) risk *= 1.5;
          if (values.tabac) risk *= 1.2;
          if (values.corticoides) risk *= 1.5;
          if (values.alcool) risk *= 1.2;
          if (values.dmo && values.dmo < -2.5) risk *= 1.5;
          return Math.min(Math.round(risk * 10) / 10, 50);
        },
        interpretation: (score) => {
          if (score < 10) return { level: "faible", text: `Risque faible (${score}%)`, color: "text-green-400", action: "Mesures hygiéno-diététiques, calcium, vitamine D" };
          if (score < 20) return { level: "modere", text: `Risque modéré (${score}%)`, color: "text-yellow-400", action: "DMO si non faite, traitement selon T-score" };
          return { level: "eleve", text: `Risque élevé (${score}%)`, color: "text-red-400", action: "Traitement anti-ostéoporotique recommandé" };
        }
      }
    ]
  },

  // ============================================
  // 9. GASTRO-ENTÉROLOGIE
  // ============================================
  gastroenterologie: {
    name: "Gastro-entérologie",
    icon: "Utensils",
    color: "from-yellow-600 to-amber-600",
    scores: [
      {
        id: "fib4",
        name: "FIB-4",
        fullName: "Fibrosis-4 Index",
        description: "Évaluation de la fibrose hépatique",
        cotation: { code: "Bio", libelle: "ASAT, ALAT, plaquettes", tarif: 15.00 },
        fields: [
          { id: "age", label: "Âge (ans)", type: "number", min: 18, max: 100 },
          { id: "asat", label: "ASAT (UI/L)", type: "number", min: 5, max: 500 },
          { id: "alat", label: "ALAT (UI/L)", type: "number", min: 5, max: 500 },
          { id: "plaquettes", label: "Plaquettes (×10⁹/L)", type: "number", min: 50, max: 500 }
        ],
        calculate: (values) => {
          return Math.round((values.age * values.asat / (values.plaquettes * Math.sqrt(values.alat))) * 100) / 100;
        },
        interpretation: (score) => {
          if (score < 1.3) return { level: "faible", text: `Fibrose peu probable (FIB-4: ${score})`, color: "text-green-400", action: "VPN 90% pour fibrose avancée" };
          if (score < 2.67) return { level: "indetermine", text: `Zone grise (FIB-4: ${score})`, color: "text-yellow-400", action: "FibroScan ou ELF test recommandé" };
          return { level: "eleve", text: `Fibrose avancée probable (FIB-4: ${score})`, color: "text-red-400", action: "Avis hépatologue, FibroScan, surveillance CHC" };
        }
      },
      {
        id: "child_pugh",
        name: "Child-Pugh",
        fullName: "Score de Child-Pugh",
        description: "Sévérité de la cirrhose",
        cotation: null,
        fields: [
          { id: "encephalopathie", label: "Encéphalopathie", type: "select", options: [
            { value: 1, label: "Absente" }, { value: 2, label: "Grade I-II" }, { value: 3, label: "Grade III-IV" }
          ]},
          { id: "ascite", label: "Ascite", type: "select", options: [
            { value: 1, label: "Absente" }, { value: 2, label: "Modérée (contrôlée)" }, { value: 3, label: "Tendue (réfractaire)" }
          ]},
          { id: "bilirubine", label: "Bilirubine (µmol/L)", type: "select", options: [
            { value: 1, label: "< 35" }, { value: 2, label: "35-50" }, { value: 3, label: "> 50" }
          ]},
          { id: "albumine", label: "Albumine (g/L)", type: "select", options: [
            { value: 1, label: "> 35" }, { value: 2, label: "28-35" }, { value: 3, label: "< 28" }
          ]},
          { id: "tp", label: "TP (%)", type: "select", options: [
            { value: 1, label: "> 50%" }, { value: 2, label: "40-50%" }, { value: 3, label: "< 40%" }
          ]}
        ],
        interpretation: (score) => {
          if (score <= 6) return { level: "A", text: `Child A (${score})`, color: "text-green-400", action: "Cirrhose compensée. Survie 1 an: 100%" };
          if (score <= 9) return { level: "B", text: `Child B (${score})`, color: "text-yellow-400", action: "Cirrhose modérément décompensée. Survie 1 an: 80%" };
          return { level: "C", text: `Child C (${score})`, color: "text-red-400", action: "Cirrhose sévèrement décompensée. Survie 1 an: 45%. Évaluation transplantation" };
        }
      },
      {
        id: "meld",
        name: "MELD",
        fullName: "Model for End-stage Liver Disease",
        description: "Sévérité de l'insuffisance hépatique",
        cotation: null,
        fields: [
          { id: "bilirubine", label: "Bilirubine (mg/dL)", type: "number", min: 0.5, max: 50, step: 0.1 },
          { id: "creatinine", label: "Créatinine (mg/dL)", type: "number", min: 0.5, max: 15, step: 0.1 },
          { id: "inr", label: "INR", type: "number", min: 0.8, max: 10, step: 0.1 },
          { id: "dialyse", label: "Dialyse ≥2 fois/semaine", type: "boolean" }
        ],
        calculate: (values) => {
          const bili = Math.max(values.bilirubine, 1);
          let creat = Math.max(values.creatinine, 1);
          if (values.dialyse) creat = 4;
          const inr = Math.max(values.inr, 1);
          return Math.round(10 * (0.957 * Math.log(creat) + 0.378 * Math.log(bili) + 1.12 * Math.log(inr) + 0.643));
        },
        interpretation: (score) => {
          if (score < 10) return { level: "faible", text: `MELD faible (${score})`, color: "text-green-400", action: "Mortalité 3 mois: <2%" };
          if (score < 20) return { level: "modere", text: `MELD modéré (${score})`, color: "text-yellow-400", action: "Mortalité 3 mois: 6%" };
          if (score < 30) return { level: "eleve", text: `MELD élevé (${score})`, color: "text-orange-400", action: "Mortalité 3 mois: 20%. Évaluation transplantation" };
          return { level: "tres_eleve", text: `MELD très élevé (${score})`, color: "text-red-600", action: "Mortalité 3 mois: 50-70%. Transplantation urgente" };
        }
      },
      {
        id: "glasgow_blatchford",
        name: "Glasgow-Blatchford",
        fullName: "Glasgow-Blatchford Bleeding Score",
        description: "Nécessité d'intervention en hémorragie digestive haute",
        cotation: null,
        fields: [
          { id: "uree", label: "Urée sanguine (mmol/L)", type: "select", options: [
            { value: 0, label: "< 6.5" }, { value: 2, label: "6.5-7.9" }, { value: 3, label: "8.0-9.9" }, { value: 4, label: "10.0-24.9" }, { value: 6, label: "≥ 25" }
          ]},
          { id: "hb_homme", label: "Hémoglobine homme (g/dL)", type: "select", options: [
            { value: 0, label: "≥ 13" }, { value: 1, label: "12.0-12.9" }, { value: 3, label: "10.0-11.9" }, { value: 6, label: "< 10" }
          ]},
          { id: "hb_femme", label: "Hémoglobine femme (g/dL)", type: "select", options: [
            { value: 0, label: "≥ 12" }, { value: 1, label: "10.0-11.9" }, { value: 6, label: "< 10" }
          ]},
          { id: "pas", label: "PAS (mmHg)", type: "select", options: [
            { value: 0, label: "≥ 110" }, { value: 1, label: "100-109" }, { value: 2, label: "90-99" }, { value: 3, label: "< 90" }
          ]},
          { id: "fc", label: "FC ≥ 100/min", type: "boolean", points: 1 },
          { id: "melena", label: "Méléna", type: "boolean", points: 1 },
          { id: "syncope", label: "Syncope", type: "boolean", points: 2 },
          { id: "hepatopathie", label: "Hépatopathie", type: "boolean", points: 2 },
          { id: "insuffisance_cardiaque", label: "Insuffisance cardiaque", type: "boolean", points: 2 }
        ],
        interpretation: (score) => {
          if (score === 0) return { level: "tres_faible", text: "Score 0 - Très faible risque", color: "text-green-400", action: "Prise en charge ambulatoire possible" };
          if (score <= 5) return { level: "faible", text: `Risque faible (${score})`, color: "text-yellow-400", action: "Hospitalisation, endoscopie programmée" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "Endoscopie urgente (<12h), réanimation si besoin" };
        }
      }
    ]
  },

  // ============================================
  // 10. NÉPHROLOGIE
  // ============================================
  nephrologie: {
    name: "Néphrologie",
    icon: "Droplet",
    color: "from-cyan-600 to-blue-600",
    scores: [
      {
        id: "ckd_epi",
        name: "CKD-EPI",
        fullName: "Chronic Kidney Disease Epidemiology Collaboration",
        description: "Estimation du DFG (2021, sans race)",
        cotation: { code: "Bio", libelle: "Créatinine sérique", tarif: 2.70 },
        fields: [
          { id: "creatinine", label: "Créatinine (µmol/L)", type: "number", min: 30, max: 1500 },
          { id: "age", label: "Âge (ans)", type: "number", min: 18, max: 100 },
          { id: "sexe", label: "Sexe", type: "select", options: [{ value: "homme", label: "Homme" }, { value: "femme", label: "Femme" }] }
        ],
        calculate: (values) => {
          const SCr = values.creatinine / 88.4; // µmol/L to mg/dL
          const kappa = values.sexe === "femme" ? 0.7 : 0.9;
          const alpha = values.sexe === "femme" ? -0.241 : -0.302;
          const multi = values.sexe === "femme" ? 1.012 : 1;
          let dfg = 142 * Math.pow(Math.min(SCr / kappa, 1), alpha) * Math.pow(Math.max(SCr / kappa, 1), -1.200) * Math.pow(0.9938, values.age) * multi;
          return Math.round(dfg);
        },
        interpretation: (dfg) => {
          if (dfg >= 90) return { level: "G1", text: `G1 - DFG normal (${dfg} mL/min)`, color: "text-green-400", action: "Pas d'IRC (chercher albuminurie)" };
          if (dfg >= 60) return { level: "G2", text: `G2 - IRC légère (${dfg} mL/min)`, color: "text-yellow-400", action: "Surveillance annuelle, contrôle FDR" };
          if (dfg >= 45) return { level: "G3a", text: `G3a - IRC modérée (${dfg} mL/min)`, color: "text-orange-400", action: "Suivi néphrologique, adaptation médicaments" };
          if (dfg >= 30) return { level: "G3b", text: `G3b - IRC modérée-sévère (${dfg} mL/min)`, color: "text-orange-500", action: "Suivi néphrologique rapproché" };
          if (dfg >= 15) return { level: "G4", text: `G4 - IRC sévère (${dfg} mL/min)`, color: "text-red-400", action: "Préparation suppléance" };
          return { level: "G5", text: `G5 - IRC terminale (${dfg} mL/min)`, color: "text-red-600", action: "Dialyse ou transplantation" };
        }
      }
    ]
  },

  // ============================================
  // 11. URGENCES / TRAUMATOLOGIE
  // ============================================
  urgences: {
    name: "Urgences / Traumatologie",
    icon: "Siren",
    color: "from-red-600 to-orange-600",
    scores: [
      {
        id: "ottawa_ankle",
        name: "Ottawa Ankle Rules",
        fullName: "Règles d'Ottawa pour la cheville",
        description: "Indication de radiographie après traumatisme de cheville",
        cotation: { code: "NZQK001", libelle: "Radio cheville", tarif: 17.33 },
        fields: [
          { id: "age", label: "Âge 18-55 ans", type: "boolean" },
          { id: "malleole_ext_6cm", label: "Douleur sur les 6 derniers cm de la malléole externe", type: "boolean" },
          { id: "malleole_ext_pointe", label: "Douleur à la pointe de la malléole externe", type: "boolean" },
          { id: "malleole_int_6cm", label: "Douleur sur les 6 derniers cm de la malléole interne", type: "boolean" },
          { id: "malleole_int_pointe", label: "Douleur à la pointe de la malléole interne", type: "boolean" },
          { id: "incapacite_appui", label: "Incapacité de faire 4 pas immédiatement et aux urgences", type: "boolean" }
        ],
        interpretation: (values) => {
          const needXray = !values.age || values.malleole_ext_pointe || values.malleole_int_pointe || values.incapacite_appui;
          if (!needXray) return { level: "negatif", text: "Ottawa négatif", color: "text-green-400", action: "Radiographie non indiquée. Traitement symptomatique" };
          return { level: "positif", text: "Ottawa positif", color: "text-red-400", action: "Radiographie de cheville indiquée" };
        }
      },
      {
        id: "alvarado",
        name: "Alvarado",
        fullName: "Score d'Alvarado",
        description: "Suspicion d'appendicite aiguë",
        cotation: null,
        fields: [
          { id: "migration", label: "Migration de la douleur vers la FID", type: "boolean", points: 1 },
          { id: "anorexie", label: "Anorexie", type: "boolean", points: 1 },
          { id: "nausees", label: "Nausées/vomissements", type: "boolean", points: 1 },
          { id: "douleur_fid", label: "Douleur à la palpation en FID", type: "boolean", points: 2 },
          { id: "rebond", label: "Douleur au rebond", type: "boolean", points: 1 },
          { id: "fievre", label: "Température ≥ 37.3°C", type: "boolean", points: 1 },
          { id: "leucocytose", label: "Leucocytes > 10 G/L", type: "boolean", points: 2 },
          { id: "polynucleose", label: "Polynucléose > 75%", type: "boolean", points: 1 }
        ],
        interpretation: (score) => {
          if (score <= 4) return { level: "faible", text: `Appendicite peu probable (${score}/10)`, color: "text-green-400", action: "Surveillance, autre diagnostic" };
          if (score <= 6) return { level: "modere", text: `Appendicite possible (${score}/10)`, color: "text-yellow-400", action: "Scanner abdominal recommandé" };
          if (score <= 8) return { level: "probable", text: `Appendicite probable (${score}/10)`, color: "text-orange-400", action: "Avis chirurgical ± scanner" };
          return { level: "tres_probable", text: `Appendicite très probable (${score}/10)`, color: "text-red-400", action: "Chirurgie, scanner si doute" };
        }
      },
      {
        id: "mcisaac",
        name: "McIsaac",
        fullName: "Score de McIsaac",
        description: "Probabilité d'angine streptococcique",
        cotation: { code: "HLJD010", libelle: "TDR Streptocoque A", tarif: 6.27 },
        fields: [
          { id: "fievre", label: "Température > 38°C", type: "boolean", points: 1 },
          { id: "toux_absente", label: "Absence de toux", type: "boolean", points: 1 },
          { id: "adp", label: "Adénopathies cervicales antérieures sensibles", type: "boolean", points: 1 },
          { id: "exsudat", label: "Atteinte amygdalienne (hypertrophie ou exsudat)", type: "boolean", points: 1 },
          { id: "age", label: "Âge", type: "select", options: [
            { value: 1, label: "3-14 ans" }, { value: 0, label: "15-44 ans" }, { value: -1, label: "≥ 45 ans" }
          ]}
        ],
        interpretation: (score) => {
          if (score <= 0) return { level: "tres_faible", text: `Risque très faible (${score})`, color: "text-green-400", action: "TDR non indiqué, traitement symptomatique" };
          if (score <= 1) return { level: "faible", text: `Risque faible (${score})`, color: "text-green-400", action: "TDR non indiqué selon HAS" };
          if (score <= 2) return { level: "modere", text: `Risque modéré (${score})`, color: "text-yellow-400", action: "TDR recommandé" };
          return { level: "eleve", text: `Risque élevé (${score})`, color: "text-red-400", action: "TDR recommandé (si + : antibiothérapie)" };
        }
      }
    ]
  },

  // ============================================
  // 12. ONCOLOGIE
  // ============================================
  oncologie: {
    name: "Oncologie",
    icon: "Activity",
    color: "from-gray-600 to-slate-600",
    scores: [
      {
        id: "ecog",
        name: "ECOG / OMS",
        fullName: "Eastern Cooperative Oncology Group Performance Status",
        description: "État général du patient cancéreux",
        cotation: null,
        options: [
          { label: "0 - Activité normale sans restriction", value: 0 },
          { label: "1 - Activité physique diminuée mais ambulatoire", value: 1 },
          { label: "2 - Ambulatoire >50% du temps, capable de se prendre en charge", value: 2 },
          { label: "3 - Alité ou au fauteuil >50% du temps, aide partielle nécessaire", value: 3 },
          { label: "4 - Complètement dépendant, alité en permanence", value: 4 }
        ],
        interpretation: (score) => {
          if (score === 0) return { level: "excellent", text: "ECOG 0 - Excellent état général", color: "text-green-400", action: "Éligible à tous les traitements" };
          if (score === 1) return { level: "bon", text: "ECOG 1 - Bon état général", color: "text-green-400", action: "Éligible à la plupart des traitements" };
          if (score === 2) return { level: "modere", text: "ECOG 2 - État général altéré", color: "text-yellow-400", action: "Chimiothérapie adaptée, discussion RCP" };
          if (score === 3) return { level: "mauvais", text: "ECOG 3 - Mauvais état général", color: "text-orange-400", action: "Soins de support, chimiothérapie à discuter" };
          return { level: "tres_mauvais", text: "ECOG 4 - Très mauvais état général", color: "text-red-400", action: "Soins palliatifs, chimiothérapie contre-indiquée" };
        }
      },
      {
        id: "karnofsky",
        name: "Karnofsky",
        fullName: "Karnofsky Performance Status",
        description: "Autonomie du patient cancéreux",
        cotation: null,
        options: [
          { label: "100% - Normal, pas de plainte", value: 100 },
          { label: "90% - Activité normale, symptômes mineurs", value: 90 },
          { label: "80% - Activité normale avec effort", value: 80 },
          { label: "70% - Incapable de travailler, autonome", value: 70 },
          { label: "60% - Aide occasionnelle nécessaire", value: 60 },
          { label: "50% - Aide considérable requise", value: 50 },
          { label: "40% - Handicapé, aide spécialisée", value: 40 },
          { label: "30% - Sévèrement handicapé, hospitalisation", value: 30 },
          { label: "20% - Très malade, soins intensifs", value: 20 },
          { label: "10% - Moribond", value: 10 }
        ],
        interpretation: (score) => {
          if (score >= 80) return { level: "bon", text: `Bon état (KPS ${score}%)`, color: "text-green-400", action: "Éligible aux traitements actifs" };
          if (score >= 60) return { level: "modere", text: `État modéré (KPS ${score}%)`, color: "text-yellow-400", action: "Traitement adapté à discuter" };
          if (score >= 40) return { level: "altere", text: `État altéré (KPS ${score}%)`, color: "text-orange-400", action: "Soins de support prioritaires" };
          return { level: "mauvais", text: `État très altéré (KPS ${score}%)`, color: "text-red-400", action: "Soins palliatifs" };
        }
      }
    ]
  }
};

// Export helper functions
export const getAllScores = () => {
  const all = [];
  Object.entries(SCORES_DATABASE).forEach(([catKey, category]) => {
    category.scores.forEach(score => {
      all.push({ ...score, categoryKey: catKey, categoryName: category.name });
    });
  });
  return all;
};

export const searchScores = (query) => {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  return getAllScores().filter(score => 
    score.name.toLowerCase().includes(q) ||
    score.fullName.toLowerCase().includes(q) ||
    score.description.toLowerCase().includes(q)
  );
};

export const getScoreById = (id) => {
  for (const category of Object.values(SCORES_DATABASE)) {
    const score = category.scores.find(s => s.id === id);
    if (score) return score;
  }
  return null;
};
